import axios from '@/axiosFieldwork'
import { createPinia, defineStore } from 'pinia'

import type {
  CSAExchangeType,
  HolidaySpecificationType,
  ReferenceDataRecordType,
  ReferenceDataResponseType
} from '@/types/SM/referenceData'

import type {
  BucketUtilisationRecordType,
  NextAvailableTimeslotApiResponseType,
  NextAvailableTimeslotTableDataType,
  PresetFilterType,
  CapacityBucketsSummaryResponseType,
  MonthlyCapacityCalendarDataType,
  ViewMonthlyCapacityAPIResponseType,
  SetDefaultViewPayloadType,
  SearchFilterType,
  SearchCapacityBucketType,
  ScheduleParametersType,
  FilterDataType,
  RAPBucketRow
} from '@/types/CM/viewCapacity'
import type { OptionType } from '@/types/SM/formFields'
import {
  dateTimeNzFormat,
  formatDayOfWeek,
  formatTimeObjectTo24Hrs,
  getActivationPeriodAsNumber,
  getTimestampSuffixForFilename
} from '@/helpers/genericFormatting'

import type { AxiosError, AxiosResponse } from 'axios'

import type {
  BaselineCapacityTableDataType,
  BaselineCapacityTimeslotApiResponseType,
  BaselineCapacityTimeslotsUpdatePayloadType,
  BaselineCapacityTimeslotType,
  PaginationType,
  PushToActivePreviewTableType,
  TableDataType,
  TimeResult,
  TimeslotAvailabilityPayloadType,
  TimeslotAvailabilityType,
  TimeSlotPreviewTableType,
  TimeSlotsAPIResponseType,
  TimeslotUpdatePayloadType,
  TimeslotUpdateType
} from '@/types/CM/updateCapacity'

import type {
  ScheduleBucketAPIResponseType,
  ScheduleDiagramBlockPropsType,
  ViewScheduleAPIResponseType,
  RuleType
} from '@/types/CM/viewSchedule'

import {
  mapUpdateCapacityResponseToTableData,
  mapUpdateTimeslotAvailabilityResponseToTableData,
  mapViewMonthlyCapacityResponseToCalendarData,
  mapNextAvailableTimeslotsResponseToTableData,
  mapUpdateTimeslotAvailabilityResponseToRecordType,
  mapBaselineCapacityResponseToTableData,
  sortBaselineCapacityTimeslotsByWeekday,
  getPresetFiltersForWorkSupplier,
  mapRAPResponseToTableRow
} from '@/helpers/CM/dataMapping'

import { DateTime } from 'luxon'
import { userCompanyIsChorus, userCompanyIsUCG, userHasCMAdminRole, userHasCMUpdaterRole } from '@/helpers/azureFieldwork'
import { weekdayNumberFromName } from '@/helpers/SM/lists'
import type { APIErrorResponseType, FiltersType } from '@/types/CM/general'
import type {
  LeadTimeRecordType,
  LeadTimeItemType,
  LeadTimesTableData,
  UpdateLeadTimesPayload
} from '@/types/CM/leadTimes'
import {
  getExchangesFromLeadTimes,
  getJobProfilesFromLeadTimes,
  getLeadTimesTableData, prepareLeadTimeTableData
} from '@/helpers/CM/leadTimesDataMapping'
import type {
  BucketWIthCutOffTimesRecordType, CutOfTimeInternalListItemType,
  UpdateCutOffTimesForBucketPayload,
  ViewBucketsWithCutOffTimesApiResponse
} from '@/types/CM/cutOffTimes'
import type { ActivityRecordType, AlertBannerError, ErrorRecordType } from '@/types/CM/errorsAndActivities'
import { getApiUTCDateTimeString } from '@/helpers/genericHelpers'

export const activePinia = createPinia()

export const useMainCMStore = defineStore({
  id: 'mainCMStore',
  state: () => ({
    workSupplier: '',
    workSupplierLabel: '' as string,
    referenceData: null as null | ReferenceDataResponseType,
    previouslySelectedFilters: null as null | FilterDataType,

    globalErrorPopup: {
      showPopup: false,
      title: 'An error occurred',
      errorMessage: 'Something went wrong. Try again later.',
      errorDetails: {} as object | null
    },

    alertBanner: {
      errors: [] as AlertBannerError[]
    },

    viewCapacity: {
      showSpinner: false,
      showBucketsSpinner: false,
      showUpdateSpinner: false,
      showDeletedPresetFilterPopup: false,

      showDefaultViewUpdateStatus: '' as '' | 'success' | 'error',
      disableSecondaryFilters: true,
      defaultFiltersSet: false,

      bucketsUtilisationRecords: null as null | BucketUtilisationRecordType[],
      referenceOptions: {
        filters: [] as PresetFilterType[],
        bucketOptions: [] as OptionType[]
      },

      pagination: {
        itemsPerPage: 25,
        totalRecords: 0,
        totalPages: 0,
        currentPage: 1
      } as PaginationType,

      sorting: {
        sortingOrder: '' as '' | 'sortAscending' | 'sortDescending',
        sortingField: '' as '' | 'workType' | 'name' | 'shortName' | 'totalUtilisation' | 'dailyUtilisation' | 'nextAvailableAppointmentStartDateTime' | 'nextAvailableTimeslot'
      },

      filters: {
        view: 'capacity',
        offset: 0,
        count: 25,
        // ------
        selectedFilterMode: '' as '' | 'bucketName' | 'presetFilter' | 'csaJobProfileExchange',
        selectedPresetFilter: [] as string[],
        selectedWorkTypes: [] as string[],
        selectedBuckets: [] as string [],
        selectedCSA: [] as string [],
        selectedJobProfiles: [] as string [],
        selectedExchanges: [] as string []
      }
    },

    presetFiltersPage: {
      showSpinner: false,

      viewMode: 'view' as 'edit' | 'view',
      selectedFilterLabel: 'Buckets',

      createPopup: {
        show: false,
        name: '',
        showSpinner: false,
        showValidationErrors: false,
        apiErrors: [] as string[]
      },
      renamePopup: {
        show: false,
        showSpinner: false,
        name: '',
        showValidationErrors: false,
        apiErrors: [] as string[]
      },

      deletePopup: {
        show: false,
        showSpinner: false
      },

      previewTable: {
        showSpinner: false,

        currentActiveFilter: undefined as undefined | OptionType,

        filters: {
          filterId: '' as string,
          view: 'summary',
          offset: 0,
          count: 25
        },
        pagination: {
          itemsPerPage: 25,
          totalRecords: 0,
          totalPages: 0,
          currentPage: 1
        } as PaginationType
      },

      addRemoveBucketsView: {
        showSpinner: false,
        showSaveFilterSuccessPopup: false,
        disableSecondaryFilters: false,

        referenceOptions: {
          bucketOptions: [] as OptionType[]
        },

        availableBuckets: [] as OptionType[],
        presetFilterBuckets: [] as OptionType[],

        filters: {
          view: 'summary',
          offset: 0,
          count: 10000,
          // ------
          selectedFilterMode: 'bucketName' as 'bucketName' | 'presetFilter' | 'csaJobProfileExchange',
          selectedPresetFilter: [] as string[],
          selectedWorkTypes: [] as string[],
          selectedBuckets: [] as string [],
          selectedCSA: [] as string [],
          selectedJobProfiles: [] as string [],
          selectedExchanges: [] as string []
        }
      },

      buckets: null as null | CapacityBucketsSummaryResponseType,
      referenceOptions: {
        filters: [] as PresetFilterType[]
      }

    },

    viewMonthlyCapacity: {
      showTableSpinner: false,
      showBucketsSpinner: false,

      monthlyCapacityApiResponse: null as null | ViewMonthlyCapacityAPIResponseType,
      monthlyCapacityCalendarData: null as null | MonthlyCapacityCalendarDataType,
      referenceOptions: {
        filters: [] as PresetFilterType[],
        bucketOptions: [] as OptionType[]
      },

      filters: {
        view: 'capacity',
        offset: 0,
        count: 25,

        commonStartDateForCalendars: new Date(),
        selectedBucketActivationPeriod: 90 as number,

        selectedFilterMode: 'bucketName' as '' | 'bucketName',
        selectedBuckets: [] as string[]
      }
    },

    viewNextAvailableTimeSlot: {
      showTableSpinner: false,
      showBucketsSpinner: false,
      disableSecondaryFilters: false,

      timeslotsApiResponse: null as null | NextAvailableTimeslotApiResponseType,
      timeslotsTableData: null as null | NextAvailableTimeslotTableDataType,

      referenceOptions: {
        filters: [] as PresetFilterType[],
        bucketOptions: [] as OptionType[]
      },

      pagination: {
        itemsPerPage: 25,
        totalRecords: 0,
        totalPages: 0,
        currentPage: 1
      } as PaginationType,

      sorting: {
        sortingOrder: 'sortDescending' as 'sortAscending' | 'sortDescending',
        sortingField: 'nextAvailableTimeslotDuration' as 'name' | 'shortName' | 'nextAvailableTimeslot' | 'nextAvailableTimeslotDuration'
      },

      filters: {
        view: 'capacity',
        offset: 0,
        count: 25,
        // ------
        selectedFilterMode: '' as '' | 'bucketName' | 'presetFilter' | 'csaJobProfileExchange',
        selectedPresetFilter: [] as string[],
        selectedWorkTypes: [] as string[],
        selectedBuckets: [] as string [],
        selectedCSA: [] as string [],
        selectedJobProfiles: [] as string [],
        selectedExchanges: [] as string []
      }
    },

    updateCapacity: {
      showBucketsSpinner: false,
      showBucketsTableSpinner: false,
      showUpdateSpinner: false,
      showTableSpinner: false,
      showSuccessPopup: false,
      showBulkEditPopup: false,

      showBulkEditTimeslotPreviewPopup: false,
      showBulkEditPreviewWarningMessage: false,

      tableIsInEditMode: false,
      disableSecondaryFilters: false,

      bucketsUtilisationRecords: null as null | BucketUtilisationRecordType[],
      apiResponse: null as null | TimeSlotsAPIResponseType,
      originalMappedTableData: {} as TableDataType,
      currentMappedTableData: {} as TableDataType,

      updateTimeSlotCapacityPayload: {} as TimeslotUpdatePayloadType,
      showUpdateMessage: '' as '' | 'success' | 'error',
      updatedCapacityBucketIds: [] as string[],

      referenceOptions: {
        filters: [] as PresetFilterType[],
        bucketOptions: [] as OptionType[],
        bucketOptionsForEditing: [] as OptionType[]
      },

      pagination: {
        itemsPerPage: 10,
        totalRecords: 0,
        totalPages: 0,
        currentPage: 1
      } as PaginationType,

      sorting: {
        sortingOrder: '' as 'sortAscending' | 'sortDescending',
        sortingField: '' as 'workType' | 'name' | 'shortName'
      },

      filters: {
        view: 'capacity',
        offset: 0,
        count: 10,

        selectedFilterMode: '' as '' | 'bucketName' | 'presetFilter' | 'csaJobProfileExchange',
        selectedPresetFilter: [] as string[],
        selectedWorkTypes: [] as string[],
        selectedBuckets: [] as string [],
        selectedCSA: [] as string [],
        selectedJobProfiles: [] as string [],
        selectedExchanges: [] as string [],

        selectedOptionsForBucketsList: [] as string[],
        selectedBucketIdsForEditTableDropdown: [] as string[],
        selectedBucketIdForEditingUpdateCapacity: '' as string,

        selectedDays: [] as string[],
        selectedStartDate: new Date() as Date,
        selectedEndDate: new Date() as Date | null,
        selectedBucketActivationPeriod: 90 as number
      },

      bulkEdit: {
        currentMappedTableData: {} as TableDataType,
        bulkEditTimeRangeOptions: [] as OptionType[],
        bulkEditMaxTotalUnits: 0 as number,
        selectedBulkEditOption: 'Exact number' as 'Exact number' | 'Add units' | 'Remove units',
        selectedBulkEditTimeRanges: [] as string[]
      }
    },

    updateTimeslotAvailability: {
      showSpinner: false,
      showBucketsSpinner: false,
      showUpdateSpinner: false,
      showPreviewUpdatePopup: false,
      showUpdatePopup: false,
      disableSecondaryFilters: false,

      showUpdateMessage: '' as '' | 'success' | 'error',
      updatedCapacityBucketIds: [] as string[],

      bucketsUtilisationRecords: null as null | BucketUtilisationRecordType[],
      timeslotPreviewApiResponse: null as null | TimeSlotsAPIResponseType,
      timeslotAvailabilityPreviewData: {} as TimeSlotPreviewTableType,
      referenceOptions: {
        filters: [] as PresetFilterType[],
        bucketOptions: [] as OptionType[]
      },

      sorting: {
        sortingOrder: '' as '' | 'sortAscending' as 'sortAscending' | 'sortDescending',
        sortingField: '' as '' | 'workType' | 'name' | 'shortName' | 'totalUtilisation' | 'dailyUtilisation' | 'nextAvailableAppointmentStartDateTime' | 'nextAvailableTimeslot'
      },

      filters: {
        view: 'timeslot',
        offset: 0,
        count: 25,

        selectedFilterMode: '' as '' | 'bucketName' | 'presetFilter' | 'csaJobProfileExchange',
        selectedPresetFilter: [] as string[],
        selectedWorkTypes: [] as string[],
        selectedBuckets: [] as string [],
        selectedBucketsToChange: [] as string [],
        selectedCSA: [] as string [],
        selectedJobProfiles: [] as string [],
        selectedExchanges: [] as string [],
        selectedBucketActivationPeriod: 90 as number
      },

      pagination: {
        itemsPerPage: 25,
        totalRecords: 0,
        totalPages: 0,
        currentPage: 1
      } as PaginationType,

      changeAvailabilityPopup: {
        startDate: new Date(),
        endDate: new Date() as Date | null,
        startTime: null as null | TimeResult,
        endTime: null as null | TimeResult,
        days: [] as string[],

        setInactive: false,
        setCapacityToZero: false,
        setAvailability: 'Available' as TimeslotAvailabilityType
      }
    },

    updateBaselineCapacity: {
      showSpinner: false,
      showBucketsSpinner: false,
      showCurrentBaselineTableSpinner: false,
      showPushToActiveSpinner: false,
      disableSecondaryFilters: false,

      showPushToActivePopup: false,
      showPreviewTablePopup: false,

      showSuccessPopup: false,

      baselineCapacityTimeslotsApiResponse: null as null | BaselineCapacityTimeslotApiResponseType,
      currentBaselineCapacityTimeslotsTableData: null as null | BaselineCapacityTableDataType,
      originalBaselineCapacityTimeslotsTableData: null as null | BaselineCapacityTableDataType,

      previewApiResponse: null as null | TimeSlotsAPIResponseType,
      previewTableData: null as null | PushToActivePreviewTableType,

      tableIsInEditMode: false,

      pushToActiveDataResultMsg: '' as '' | 'success' | 'error',
      updatedCapacityBucketIds: [] as string[],

      bucketsUtilisationRecords: null as null | BucketUtilisationRecordType[],
      referenceOptions: {
        filters: [] as PresetFilterType[],
        bucketOptions: [] as OptionType[],
        bucketOptionsForEditing: [] as OptionType[]
      },

      sorting: {
        sortingOrder: '' as 'sortAscending' | 'sortDescending',
        sortingField: '' as 'workType' | 'name' | 'shortName' | 'totalUtilisation' | 'dailyUtilisation' | 'nextAvailableAppointmentStartDateTime' | 'nextAvailableTimeslot'
      },

      filters: {
        view: 'capacity',
        offset: 0,
        count: 10,

        selectedFilterMode: '' as '' | 'bucketName' | 'presetFilter' | 'csaJobProfileExchange',
        selectedPresetFilter: [] as string[],
        selectedWorkTypes: [] as string[],
        selectedBuckets: [] as string[],
        selectedBucketIdForEditing: '' as string,
        selectedCSA: [] as string[],
        selectedJobProfiles: [] as string[],
        selectedExchanges: [] as string[],
        selectedPopupStartDate: new Date() as Date,
        selectedWeekDays: [] as string[],
        selectedBucketActivationPeriod: 90 as number
      },

      pagination: {
        itemsPerPage: 10,
        totalRecords: 0,
        totalPages: 0,
        currentPage: 1
      } as PaginationType
    },

    updateRAP: {
      showSpinner: false,
      showBucketsSpinner: false,

      disableSecondaryFilters: true,

      showBulkEditPopup: false,
      showBulkEditPreviewPopup: false,

      bulkEdit: {
        bulkEditRAP: 0 as number,
        showUpdateSpinner: false,
        showSuccessPopup: false
      },

      originalBuckets: null as null | RAPBucketRow[],
      currentBuckets: null as null | RAPBucketRow[],
      previewBuckets: null as null | RAPBucketRow[],

      referenceOptions: {
        filters: [] as PresetFilterType[],
        bucketOptions: [] as OptionType[]
      },

      pagination: {
        itemsPerPage: 25,
        totalRecords: 0,
        totalPages: 0,
        currentPage: 1
      } as PaginationType,

      filters: {
        view: 'summary',
        offset: 0,
        count: 25,
        selectedFilterMode: '' as '' | 'bucketName' | 'presetFilter' | 'csaJobProfileExchange',
        selectedPresetFilter: [] as string[],
        selectedWorkTypes: [] as string[],
        selectedBuckets: [] as string [],
        selectedCSA: [] as string [],
        selectedJobProfiles: [] as string [],
        selectedExchanges: [] as string []
      }
    },
    updateLeadTime: {
      showSpinner: false,
      showSuccessPopup: false,
      showBulkEditPopup: false,
      showAssureBucketsOnlyWarning: false,

      bulkEditPopup: {
        showValidationErrors: false,
        showPopup: false,
        hours: 0,
        minutes: 0
      },
      bulkEditPreviewPopup: {
        showPopup: false,
        previewLeadTimesRecords: null as null | LeadTimeItemType[],
        tableData: null as null | LeadTimesTableData,
        showSpinner: false
      },

      leadTimesRecords: null as null | LeadTimeRecordType[],
      leadTimesCellsOriginal: null as null | LeadTimeItemType[],
      tableData: null as null | LeadTimesTableData,
      selectedBucketLevel2: [] as string[],

      exchanges: [] as string[],
      jobProfiles: [] as string[],

      referenceOptions: {
        filters: [] as PresetFilterType[],
        bucketOptions: [] as OptionType[],
        level2BucketsOptions: [] as OptionType[]
      },

      filters: {
        view: 'capacity',
        offset: 0,
        count: 25,
        // ------
        selectedFilterMode: '' as '' | 'bucketName' | 'presetFilter' | 'csaJobProfileExchange',
        selectedPresetFilter: [] as string[],
        selectedWorkTypes: [] as string[],
        selectedBuckets: [] as string [],
        selectedCSA: [] as string [],
        selectedJobProfiles: [] as string [],
        selectedExchanges: [] as string []
      }
    },

    cutOffTimes: {
      showBucketsSpinner: false,
      showBucketsTableSpinner: false,
      disableSecondaryFilters: false,
      apiResponse: null as null | ViewBucketsWithCutOffTimesApiResponse,

      referenceOptions: {
        filters: [] as PresetFilterType[],
        bucketOptions: [] as OptionType[],
        bucketOptionsForEditing: [] as OptionType[]
      },

      bucketsLevel2: null as null | BucketWIthCutOffTimesRecordType[],
      pagination: {
        itemsPerPage: 25,
        totalRecords: 0,
        totalPages: 0,
        currentPage: 1
      } as PaginationType,

      bucketIsInEditMode: false,
      showSaveCutOffTimeForBucketSpinner: false,
      showSaveSuccessPopup: false,
      showRapValidationWarningPopup: false,

      bulkEditPopup: {
        cutOffRecords: [] as CutOfTimeInternalListItemType[],

        showPopup: false,
        showSpinner: false,
        showSuccessMessage: false
      },

      filters: {
        view: 'cutoff',
        offset: 0,
        count: 10,
        selectedFilterMode: '' as '' | 'bucketName' | 'presetFilter' | 'csaJobProfileExchange',
        selectedPresetFilter: [] as string[],
        selectedWorkTypes: [] as string[],
        selectedBuckets: [] as string [],
        selectedCSA: [] as string [],
        selectedJobProfiles: [] as string [],
        selectedExchanges: [] as string [],

        selectedBucketIdForEditing: '' as string,
        selectedOptionsForBucketsList: [] as string[],
        selectedBucketIdsForEditDropdown: [] as string[]
      }
    },
    viewSchedules: {
      showDiagramSpinner: false,
      apiResponse: null as null | ViewScheduleAPIResponseType,
      visibleElementsIDs: [] as string[],
      allBlocks: [] as ScheduleDiagramBlockPropsType[],
      filters: {
        workType: 'REMO',
        workSupplier: '',
        scheduleDate: '',
        statusAtDate: 'ACTIVE' as string
      },
      detailsPopup: {
        showPopup: false,
        showSpinner: false,
        title: '',
        titlePrefix: '',
        blockDetailsPopupFields: [] as OptionType[]
      }
    },
    errorsHistory: {
      errorsList: null as null | ErrorRecordType[],
      showTableSpinner: false,

      filters: {
        eventCategory: 'ERROR',
        startDate: null as null | Date,
        endDate: null as null | Date
      },

      pagination: {
        itemsPerPage: 25,
        totalRecords: 0,
        totalPages: 0,
        currentPage: 1
      } as PaginationType
    },

    activityHistory: {
      activityList: null as null | ActivityRecordType[],
      showTableSpinner: false,
      showBucketsSpinner: false,

      referenceOptions: {
        bucketOptions: [] as OptionType[]
      },

      filters: {
        eventCategory: 'ACTIVITY',
        activityType: '',
        capacityBucketId: [] as string[],
        userName: '',
        startDate: null as null | Date,
        endDate: null as null | Date
      },

      pagination: {
        itemsPerPage: 25,
        totalRecords: 0,
        totalPages: 0,
        currentPage: 1
      } as PaginationType
    }
  }),

  getters: {
    sortingParametersViewBuckets (): Map<string, string> {
      const result = new Map<string, string>()
      result.set(this.viewCapacity.sorting.sortingOrder, this.viewCapacity.sorting.sortingField)
      return result
    },

    publicHolidays (): Map<string, string> {
      const result = new Map<string, string>()
      const publicHolidays = this.referenceData
        ?.find((item: ReferenceDataRecordType) => item.name === 'PUBLIC_HOLIDAY')
        ?.specification as HolidaySpecificationType

      if (publicHolidays) {
        Object.values(publicHolidays).forEach((holidays) => {
          Object.entries(holidays).forEach(([date, holiday]) => {
            result.set(date, holiday)
          })
        })
      }

      return result
    },

    regionalHolidays (): Map<string, string> {
      const result = new Map<string, string>()
      const regionalHolidays = this.referenceData
        ?.find((item: ReferenceDataRecordType) => item.name === 'REGIONAL_ANNIVERSARY')
        ?.specification as HolidaySpecificationType

      if (regionalHolidays) {
        Object.values(regionalHolidays).forEach((holidays) => {
          Object.entries(holidays).forEach(([date, holiday]) => {
            result.set(date, holiday)
          })
        })
      }

      return result
    },

    defaultParametersGetBucketsForEditPresetFiltersPage (): Map<string, string> {
      const result = new Map<string, string>()
      result.set('offset', '0')
      result.set('count', '1000')
      if (userCompanyIsChorus()) {
        result.set('workSupplier', this.workSupplier)
      }
      result.set('view', 'summary')
      return result
    },

    queryParametersGetBucketsForEditPresetFiltersPage (): Map<string, string> {
      const result = new Map() as Map<string, string>
      const filters = this.presetFiltersPage.addRemoveBucketsView.filters

      if (filters.selectedFilterMode === 'presetFilter') {
        if (filters.selectedPresetFilter.length > 0) {
          result.set('filterIds', filters.selectedPresetFilter.join(','))
        }
      }

      if (filters.selectedFilterMode === 'csaJobProfileExchange') {
        if (filters.selectedCSA.length > 0) {
          result.set('csa', filters.selectedCSA.join(','))
        }

        if (filters.selectedJobProfiles.length > 0) {
          result.set('jobProfile', filters.selectedJobProfiles.join(','))
        }

        if (filters.selectedExchanges.length > 0) {
          result.set('exchange', filters.selectedExchanges.join(','))
        }
      }

      if (filters.selectedWorkTypes.length > 0) {
        result.set('workType', filters.selectedWorkTypes.join(','))
      }

      return result
    },

    allParametersGetBucketsForEditPresetFiltersPage (): { [k: string]: string } {
      return {
        ...Object.fromEntries(this.defaultParametersGetBucketsForEditPresetFiltersPage),
        ...Object.fromEntries(this.queryParametersGetBucketsForEditPresetFiltersPage)
      }
    },

    defaultParametersViewBuckets (): Map<string, string> {
      const result = new Map<string, string>()
      result.set('offset', this.viewCapacity.filters.offset.toString())
      result.set('count', this.viewCapacity.filters.count.toString())
      if (userCompanyIsChorus()) {
        result.set('workSupplier', this.workSupplier)
      }
      result.set('view', 'capacity')
      return result
    },

    defaultParametersViewBucketsWithCutOff (): Map<string, string> {
      const result = new Map<string, string>()
      result.set('offset', this.cutOffTimes.filters.offset.toString())
      result.set('count', this.cutOffTimes.filters.count.toString())
      if (userCompanyIsChorus()) {
        result.set('workSupplier', this.workSupplier)
      }
      result.set('view', 'cutoff')
      return result
    },

    queryParametersViewBuckets (): Map<string, string> {
      const result = new Map() as Map<string, string>
      const filters = this.viewCapacity.filters

      if (filters.selectedFilterMode === 'presetFilter') {
        if (filters.selectedPresetFilter.length > 0) {
          result.set('filterIds', filters.selectedPresetFilter.join(','))
        }
      }

      if (filters.selectedFilterMode === 'csaJobProfileExchange') {
        if (filters.selectedCSA.length > 0) {
          result.set('csa', filters.selectedCSA.join(','))
        }

        if (filters.selectedJobProfiles.length > 0) {
          result.set('jobProfile', filters.selectedJobProfiles.join(','))
        }

        if (filters.selectedExchanges.length > 0) {
          result.set('exchange', filters.selectedExchanges.join(','))
        }
      }

      if (filters.selectedWorkTypes.length > 0) {
        result.set('workType', filters.selectedWorkTypes.join(','))
      }

      return result
    },

    allParametersViewBuckets (): { [k: string]: string } {
      return {
        ...Object.fromEntries(this.defaultParametersViewBuckets),
        ...Object.fromEntries(this.queryParametersViewBuckets),
        ...Object.fromEntries(this.sortingParametersViewBuckets)
      }
    },

    queryParametersViewBucketsWithCutOff (): Map<string, string> {
      const result = new Map() as Map<string, string>
      const filters = this.cutOffTimes.filters

      if (filters.selectedFilterMode === 'presetFilter') {
        if (filters.selectedPresetFilter.length > 0) {
          result.set('filterIds', filters.selectedPresetFilter.join(','))
        }
      }

      if (filters.selectedFilterMode === 'csaJobProfileExchange') {
        if (filters.selectedCSA.length > 0) {
          result.set('csa', filters.selectedCSA.join(','))
        }

        if (filters.selectedJobProfiles.length > 0) {
          result.set('jobProfile', filters.selectedJobProfiles.join(','))
        }

        if (filters.selectedExchanges.length > 0) {
          result.set('exchange', filters.selectedExchanges.join(','))
        }
      }

      if (filters.selectedWorkTypes.length > 0) {
        result.set('workType', filters.selectedWorkTypes.join(','))
      }

      return result
    },

    allParametersViewBucketsWithCutOff (): { [k: string]: string } {
      return {
        ...Object.fromEntries(this.defaultParametersViewBucketsWithCutOff),
        ...Object.fromEntries(this.queryParametersViewBucketsWithCutOff)
      }
    },

    sortingParametersUpdateTimeslotAvailability (): Map<string, string> {
      const result = new Map<string, string>()
      result.set(this.updateTimeslotAvailability.sorting.sortingOrder, this.updateTimeslotAvailability.sorting.sortingField)
      return result
    },

    defaultParametersUpdateTimeslotAvailability (): Map<string, string> {
      const result = new Map<string, string>()
      result.set('offset', this.updateTimeslotAvailability.filters.offset.toString())
      result.set('count', this.updateTimeslotAvailability.filters.count.toString())
      if (userCompanyIsChorus()) {
        result.set('workSupplier', this.workSupplier)
      }
      result.set('view', 'summary')
      return result
    },

    queryParametersUpdateTimeslotAvailability (): Map<string, string> {
      const result = new Map() as Map<string, string>
      const filters = this.updateTimeslotAvailability.filters

      if (filters.selectedFilterMode === 'presetFilter') {
        if (filters.selectedPresetFilter.length > 0) {
          result.set('filterIds', filters.selectedPresetFilter.join(','))
        }
      }

      if (filters.selectedFilterMode === 'csaJobProfileExchange') {
        if (filters.selectedCSA.length > 0) {
          result.set('csa', filters.selectedCSA.join(','))
        }

        if (filters.selectedJobProfiles.length > 0) {
          result.set('jobProfile', filters.selectedJobProfiles.join(','))
        }

        if (filters.selectedExchanges.length > 0) {
          result.set('exchange', filters.selectedExchanges.join(','))
        }
      }

      if (filters.selectedWorkTypes.length > 0) {
        result.set('workType', filters.selectedWorkTypes.join(','))
      }

      return result
    },

    allParametersUpdateTimeslotAvailability (): { [k: string]: string } {
      return {
        ...Object.fromEntries(this.defaultParametersUpdateTimeslotAvailability),
        ...Object.fromEntries(this.queryParametersUpdateTimeslotAvailability),
        ...Object.fromEntries(this.sortingParametersUpdateTimeslotAvailability)
      }
    },

    sortingParametersUpdateBaselineCapacity (): Map<string, string> {
      const result = new Map<string, string>()
      result.set(this.updateBaselineCapacity.sorting.sortingOrder, this.updateBaselineCapacity.sorting.sortingField)
      return result
    },

    defaultParametersUpdateBaselineCapacity (): Map<string, string> {
      const result = new Map<string, string>()
      result.set('offset', this.updateBaselineCapacity.filters.offset.toString())
      result.set('count', this.updateBaselineCapacity.filters.count.toString())
      if (userCompanyIsChorus()) {
        result.set('workSupplier', this.workSupplier)
      }
      result.set('view', 'capacity')
      return result
    },

    queryParametersUpdateBaselineCapacity (): Map<string, string> {
      const result = new Map() as Map<string, string>
      const filters = this.updateBaselineCapacity.filters

      if (filters.selectedFilterMode === 'presetFilter') {
        if (filters.selectedPresetFilter.length > 0) {
          result.set('filterIds', filters.selectedPresetFilter.join(','))
        }
      }

      if (filters.selectedFilterMode === 'csaJobProfileExchange') {
        if (filters.selectedCSA.length > 0) {
          result.set('csa', filters.selectedCSA.join(','))
        }

        if (filters.selectedJobProfiles.length > 0) {
          result.set('jobProfile', filters.selectedJobProfiles.join(','))
        }

        if (filters.selectedExchanges.length > 0) {
          result.set('exchange', filters.selectedExchanges.join(','))
        }
      }

      if (filters.selectedWorkTypes.length > 0) {
        result.set('workType', filters.selectedWorkTypes.join(','))
      }

      return result
    },

    allParametersUpdateBaselineCapacity (): { [k: string]: string } {
      return {
        ...Object.fromEntries(this.defaultParametersUpdateBaselineCapacity),
        ...Object.fromEntries(this.queryParametersUpdateBaselineCapacity),
        ...Object.fromEntries(this.sortingParametersUpdateBaselineCapacity)
      }
    },

    defaultParametersNextTimeslotAvailability (): Map<string, string> {
      const result = new Map<string, string>()
      result.set('offset', this.viewNextAvailableTimeSlot.filters.offset.toString())
      result.set('count', this.viewNextAvailableTimeSlot.filters.count.toString())
      if (userCompanyIsChorus()) {
        result.set('workSupplier', this.workSupplier)
      }
      result.set('view', 'nextAvailable')
      return result
    },

    sortingParametersNextTimeslotAvailability (): Map<string, string> {
      const result = new Map<string, string>()
      result.set(this.viewNextAvailableTimeSlot.sorting.sortingOrder, this.viewNextAvailableTimeSlot.sorting.sortingField)
      return result
    },

    queryParametersNextTimeslotAvailability (): Map<string, string> {
      const result = new Map() as Map<string, string>
      const filters = this.viewNextAvailableTimeSlot.filters

      if (filters.selectedFilterMode === 'presetFilter') {
        if (filters.selectedPresetFilter.length > 0) {
          result.set('filterIds', filters.selectedPresetFilter.join(','))
        }
      }

      if (filters.selectedFilterMode === 'csaJobProfileExchange') {
        if (filters.selectedCSA.length > 0) {
          result.set('csa', filters.selectedCSA.join(','))
        }

        if (filters.selectedJobProfiles.length > 0) {
          result.set('jobProfile', filters.selectedJobProfiles.join(','))
        }

        if (filters.selectedExchanges.length > 0) {
          result.set('exchange', filters.selectedExchanges.join(','))
        }
      }

      if (filters.selectedWorkTypes.length > 0) {
        result.set('workType', filters.selectedWorkTypes.join(','))
      }

      return result
    },

    allParametersNextTimeslotAvailability (): { [k: string]: string } {
      return {
        ...Object.fromEntries(this.defaultParametersNextTimeslotAvailability),
        ...Object.fromEntries(this.queryParametersNextTimeslotAvailability),
        ...Object.fromEntries(this.sortingParametersNextTimeslotAvailability)
      }
    },

    defaultParametersUpdateCapacity (): Map<string, string> {
      const result = new Map<string, string>()
      result.set('offset', this.updateCapacity.filters.offset.toString())
      result.set('count', this.updateCapacity.filters.count.toString())
      if (userCompanyIsChorus()) {
        result.set('workSupplier', this.workSupplier)
      }
      result.set('view', 'capacity')
      return result
    },

    queryParametersUpdateCapacity (): Map<string, string> {
      const result = new Map() as Map<string, string>
      const filters = this.updateCapacity.filters

      if (filters.selectedFilterMode === 'presetFilter') {
        if (filters.selectedPresetFilter.length > 0) {
          result.set('filterIds', filters.selectedPresetFilter.join(','))
        }
      }

      if (filters.selectedFilterMode === 'csaJobProfileExchange') {
        if (filters.selectedCSA.length > 0) {
          result.set('csa', filters.selectedCSA.join(','))
        }

        if (filters.selectedJobProfiles.length > 0) {
          result.set('jobProfile', filters.selectedJobProfiles.join(','))
        }

        if (filters.selectedExchanges.length > 0) {
          result.set('exchange', filters.selectedExchanges.join(','))
        }
      }

      if (filters.selectedWorkTypes.length > 0) {
        result.set('workType', filters.selectedWorkTypes.join(','))
      }

      return result
    },

    sortingParametersUpdateCapacity (): Map<string, string> {
      const result = new Map<string, string>()
      result.set(this.updateCapacity.sorting.sortingOrder, this.updateCapacity.sorting.sortingField)
      return result
    },

    allParametersUpdateCapacity (): { [k: string]: string } {
      return {
        ...Object.fromEntries(this.defaultParametersUpdateCapacity),
        ...Object.fromEntries(this.queryParametersUpdateCapacity),
        ...Object.fromEntries(this.sortingParametersUpdateCapacity)
      }
    },

    defaultParametersUpdateRAP (): Map<string, string> {
      const result = new Map<string, string>()
      result.set('offset', this.updateRAP.filters.offset.toString())
      result.set('count', this.updateRAP.filters.count.toString())
      if (userCompanyIsChorus()) {
        result.set('workSupplier', this.workSupplier)
      }
      result.set('view', 'summary')
      return result
    },

    queryParametersUpdateRAP (): Map<string, string> {
      const result = new Map() as Map<string, string>
      const filters = this.updateRAP.filters

      if (filters.selectedFilterMode === 'presetFilter') {
        if (filters.selectedPresetFilter.length > 0) {
          result.set('filterIds', filters.selectedPresetFilter.join(','))
        }
      }

      if (filters.selectedFilterMode === 'csaJobProfileExchange') {
        if (filters.selectedCSA.length > 0) {
          result.set('csa', filters.selectedCSA.join(','))
        }

        if (filters.selectedJobProfiles.length > 0) {
          result.set('jobProfile', filters.selectedJobProfiles.join(','))
        }

        if (filters.selectedExchanges.length > 0) {
          result.set('exchange', filters.selectedExchanges.join(','))
        }
      }

      if (filters.selectedWorkTypes.length > 0) {
        result.set('workType', filters.selectedWorkTypes.join(','))
      }

      return result
    },

    allParametersUpdateRAP (): { [k: string]: string } {
      return {
        ...Object.fromEntries(this.defaultParametersUpdateRAP),
        ...Object.fromEntries(this.queryParametersUpdateRAP)
      }
    },

    updateTimeSlotCapacityPayload (): TimeslotUpdatePayloadType {
      const payload = {
        timeslotUpdate: [] as TimeslotUpdateType[]
      } as TimeslotUpdatePayloadType

      const originalTableData = this.updateCapacity.originalMappedTableData
      const currentTableData = this.updateCapacity.currentMappedTableData

      for (const timeRangeKey in currentTableData) {
        const tableRow = currentTableData[timeRangeKey]

        for (const dateKey in tableRow) {
          const cell = tableRow[dateKey]
          const originalCell = originalTableData[timeRangeKey][dateKey]

          if (cell.maxTotal !== originalCell.maxTotal) {
            payload.timeslotUpdate.push({
              id: cell.timeRangeId,
              capacity: {
                maxTotalWorkUnits: cell.maxTotal
              }
            })
          }
        }
      }

      return payload
    },

    updateTimeSlotCapacityEditBulkCapacityData (): TimeslotUpdatePayloadType {
      const payload = {
        timeslotUpdate: [] as TimeslotUpdateType[]
      } as TimeslotUpdatePayloadType

      const currentTableData = this.updateCapacity.bulkEdit.currentMappedTableData

      for (const timeRangeKey in currentTableData) {
        const tableRow = currentTableData[timeRangeKey]

        for (const dateKey in tableRow) {
          const cell = tableRow[dateKey]

          payload.timeslotUpdate.push({
            id: cell.timeRangeId,
            capacity: {
              maxTotalWorkUnits: cell.maxTotal
            }
          })
        }
      }

      return payload
    },

    updateTimeSlotAvailabilityPayload (): TimeslotAvailabilityPayloadType {
      let payload = {} as TimeslotAvailabilityPayloadType
      const popupData = this.updateTimeslotAvailability.changeAvailabilityPopup

      if (popupData.endDate && popupData.startTime && popupData.endTime) {
        const selectedBucketIds = this.updateTimeslotAvailability.filters.selectedBucketsToChange

        const startDate = DateTime.fromJSDate(popupData.startDate).toFormat('yyyy-MM-dd')
        const endDate = DateTime.fromJSDate(popupData.endDate).toFormat('yyyy-MM-dd')

        const startTimeObject = popupData.startTime
        const endTimeObject = popupData.endTime

        const formattedStartTime = formatTimeObjectTo24Hrs(startTimeObject)
        const formattedEndTime = formatTimeObjectTo24Hrs(endTimeObject)

        const selectedDaysObject = popupData.days
        const selectedDays = selectedDaysObject.length === 0 ? ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'] : selectedDaysObject

        const status = popupData.setInactive ? 'INACTIVE' : 'ACTIVE'
        const targetCapacity = popupData.setCapacityToZero ? { maxTotalWorkUnits: 0 } : {}

        payload = {
          capacityBucketIds: selectedBucketIds,
          startDate,
          endDate,
          startTime: formattedStartTime,
          endTime: formattedEndTime,
          daysOfWeek: selectedDays,
          status,
          ...targetCapacity
        }
      }

      return payload
    },

    uploadBaselineCapacityTimeslotsPayload (): BaselineCapacityTimeslotsUpdatePayloadType {
      const payload = {
        timeslotStructure: {} as { [weekdayKey: string]: BaselineCapacityTimeslotType[] }
      } as BaselineCapacityTimeslotsUpdatePayloadType

      const currentTableData = this.updateBaselineCapacity.currentBaselineCapacityTimeslotsTableData

      if (currentTableData !== null) {
        Object.entries(currentTableData).forEach(([timeRangeKey, dateData]) => {
          // timeRangeKey is in format '09:00 - 17:00'
          const [startTimeString, endTimeString] = timeRangeKey.split('-')

          const startTime = startTimeString.trim() + ':00' // e.g. 09:00:00
          const endTime = endTimeString.trim() + ':00'

          const weekdaysKeys = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']

          for (const weekdayKey of weekdaysKeys) {
            Object.entries(dateData).forEach(([dateKey, dateData]) => {
              // Check if the weekday name matches dateKey
              const isDesiredWeekDay = dateKey.toLocaleLowerCase() === weekdayKey

              if (!payload.timeslotStructure[weekdayKey]) {
                payload.timeslotStructure[weekdayKey] = []
              }

              if (isDesiredWeekDay) {
                const timeslotUpdateItem = {
                  endTime,
                  baselineCapacity: dateData?.baselineCapacity || 0,
                  startTime
                }

                payload.timeslotStructure[weekdayKey].push(timeslotUpdateItem)
              }
            })
          }
        })
      }

      return payload
    },

    saveDefaultViewForUserProfilePayload (): SetDefaultViewPayloadType {
      const filters = [] as SearchFilterType[]
      const capacityBuckets = [] as SearchCapacityBucketType[]

      const scheduleParameters: ScheduleParametersType = {
        csa: this.viewCapacity.filters.selectedCSA.join(','),
        exchange: this.viewCapacity.filters.selectedExchanges.join(','),
        jobProfile: this.viewCapacity.filters.selectedJobProfiles.join(',')
      }

      const sortingOrder = this.viewCapacity.sorting.sortingOrder
      const sortingField = this.viewCapacity.sorting.sortingField

      let sortingObject = {}
      if (sortingOrder === 'sortAscending') {
        sortingObject = { sortAscending: sortingField }
      } else if (sortingOrder === 'sortDescending') {
        sortingObject = { sortDescending: sortingField }
      }

      const count = this.viewCapacity.filters.count
      const workTypes = this.viewCapacity.filters.selectedWorkTypes

      this.viewCapacity.filters.selectedPresetFilter.forEach((filterId) => {
        const filter = this.viewCapacity.referenceOptions.filters.find((filter) => filter.id === filterId)
        const filterName = filter?.name || ''

        filters.push({
          filterId,
          filterName
        })
      })

      this.viewCapacity.filters.selectedBuckets?.forEach((capacityBucketId) => {
        const capacityBucket = this.viewCapacity.referenceOptions.bucketOptions.find((bucket) => bucket.value === capacityBucketId)
        const capacityBucketName = capacityBucket?.label || ''

        capacityBuckets.push({
          capacityBucketId,
          capacityBucketName
        })
      })

      const selectedFilterMode = this.viewCapacity.filters.selectedFilterMode

      const payload = {
        defaultSearch: {
          ...(selectedFilterMode === 'presetFilter' ? { filters } : {}),
          ...(selectedFilterMode === 'bucketName' ? { capacityBuckets } : {}),
          ...(selectedFilterMode === 'csaJobProfileExchange' ? { scheduleParameters } : {}),
          ...(count ? { count } : {}),
          ...(workTypes.length > 0 ? { workTypes } : {}),
          ...sortingObject,
          ...(this.workSupplier ? { workSupplier: this.workSupplier } : {})
        }
      } as SetDefaultViewPayloadType

      return payload
    },

    presetFiltersForViewBucketsPageByWorkSupplier (): PresetFilterType[] {
      const presetFilters = this.viewCapacity.referenceOptions.filters
      return getPresetFiltersForWorkSupplier(presetFilters, this.workSupplier)
    },

    presetFiltersForCutOffTimesPageByWorkSupplier (): PresetFilterType[] {
      const presetFilters = this.cutOffTimes.referenceOptions.filters
      return getPresetFiltersForWorkSupplier(presetFilters, this.workSupplier)
    },

    presetFiltersForPresetFiltersPageByWorkSupplier (): PresetFilterType[] {
      const presetFilters = this.presetFiltersPage.referenceOptions.filters
      return getPresetFiltersForWorkSupplier(presetFilters, this.workSupplier)
    },

    getPresetFiltersForViewNextAvailableTimeslotsPageByWorkSupplier (): PresetFilterType[] {
      const presetFilters = this.viewNextAvailableTimeSlot.referenceOptions.filters
      return getPresetFiltersForWorkSupplier(presetFilters, this.workSupplier)
    },

    getPresetFiltersForLeadTimesPageByWorkSupplier (): PresetFilterType[] {
      const presetFilters = this.updateLeadTime.referenceOptions.filters

      return getPresetFiltersForWorkSupplier(presetFilters, this.workSupplier)
    },

    presetFiltersForUpdateBaselineCapacityPageByWorkSupplier (): PresetFilterType[] {
      const presetFilters = this.updateBaselineCapacity.referenceOptions.filters
      return getPresetFiltersForWorkSupplier(presetFilters, this.workSupplier)
    },

    presetFiltersForUpdateTimeslotAvailabilityPageByWorkSupplier (): PresetFilterType[] {
      const presetFilters = this.updateTimeslotAvailability.referenceOptions.filters
      return getPresetFiltersForWorkSupplier(presetFilters, this.workSupplier)
    },

    presetFiltersForUpdateCapacityPageByWorkSupplier (): PresetFilterType[] {
      const presetFilters = this.updateCapacity.referenceOptions.filters
      return getPresetFiltersForWorkSupplier(presetFilters, this.workSupplier)
    },

    getPresetFiltersForUpdateRAPPageByWorkSupplier (): PresetFilterType[] {
      const presetFilters = this.updateRAP.referenceOptions.filters
      return getPresetFiltersForWorkSupplier(presetFilters, this.workSupplier)
    }
  },
  actions: {
    setDefaultWorkSupplier () {
      if (this.workSupplier === '') {
        let defaultWorkSupplier = 'Downer'
        let defaultWorkSupplierLabel = 'Downer Assure'

        if (userCompanyIsUCG()) {
          defaultWorkSupplier = 'UCG'
          defaultWorkSupplierLabel = 'UCG Assure'
        }

        this.workSupplier = defaultWorkSupplier
        this.workSupplierLabel = defaultWorkSupplierLabel
      }
    },

    restorePreviouslySelectedFiltersForViewBucketsPage () {
      // Restore filters from previouslySelectedFilters
      // When we navigating from other pages to view buckets page
      if (this.previouslySelectedFilters !== null) {
        this.viewCapacity.filters = {
          ...this.viewCapacity.filters,
          ...this.previouslySelectedFilters
        }
      }
    },

    restorePreviouslySelectedFiltersForBaselineCapacityPage () {
      if (this.previouslySelectedFilters !== null) {
        this.updateBaselineCapacity.filters = {
          ...this.updateBaselineCapacity.filters,
          ...this.previouslySelectedFilters
        }
      }
    },

    restorePreviouslySelectedFiltersForViewNextAvailableTimeSlotPage () {
      if (this.previouslySelectedFilters !== null) {
        this.viewNextAvailableTimeSlot.filters = {
          ...this.viewNextAvailableTimeSlot.filters,
          ...this.previouslySelectedFilters
        }
      }
    },

    restorePreviouslySelectedFiltersForUpdateCapacityPage () {
      if (this.previouslySelectedFilters !== null) {
        this.updateCapacity.filters = {
          ...this.updateCapacity.filters,
          ...this.previouslySelectedFilters
        }
      }
    },

    restorePreviouslySelectedFiltersForUpdateTimeslotAvailabilityPage () {
      if (this.previouslySelectedFilters !== null) {
        this.updateTimeslotAvailability.filters = {
          ...this.updateTimeslotAvailability.filters,
          ...this.previouslySelectedFilters
        }
      }
    },

    checkIfDefaultPresetFilterHasBeenDeleted () {
      // If the default preset filter doesn't exist anymore - we need to show popup
      // on view buckets page
      if (this.viewCapacity.filters.selectedFilterMode === 'presetFilter') {
        let allPresent = true
        const validSelectedFilters = [] as string[]
        const allFiltersIds = this.viewCapacity.referenceOptions.filters.map(filter => filter.id)
        this.viewCapacity.filters.selectedPresetFilter.forEach(filterId => {
          if (allFiltersIds.includes(filterId)) {
            validSelectedFilters.push(filterId)
          } else {
            allPresent = false
          }
        })

        if (!allPresent) {
          this.viewCapacity.showDeletedPresetFilterPopup = true
          this.viewCapacity.filters.selectedPresetFilter = validSelectedFilters
        }
      }
    },
    restorePreviouslySelectedFiltersForUpdateRAPPage () {
      if (this.previouslySelectedFilters !== null) {
        this.updateRAP.filters = {
          ...this.updateRAP.filters,
          ...this.previouslySelectedFilters
        }
      }
    },
    restorePreviouslySelectedFiltersForCutOffTimesPage () {
      if (this.previouslySelectedFilters !== null) {
        this.cutOffTimes.filters = {
          ...this.cutOffTimes.filters,
          ...this.previouslySelectedFilters
        }
      }
    },

    setDefaultFiltersForViewBucketsPage () {
      this.viewCapacity.showSpinner = true

      this.clearViewBucketsResults()

      const url = '/cams-capacity-manager/v1/profiles/me'
      return axios.get(url).then((response: AxiosResponse) => {
        if (response.status === 200) {
          const defaultSearchApiResponse = response?.data?.defaultSearch?.defaultSearch

          // Only set default filters if the user has a default view set
          if (defaultSearchApiResponse && Object.values(defaultSearchApiResponse).length === 0) {
            this.viewCapacity.showSpinner = false
            return
          }

          if (defaultSearchApiResponse !== null &&
              defaultSearchApiResponse !== undefined &&
              Object.values(defaultSearchApiResponse).length > 0) {
            this.viewCapacity.defaultFiltersSet = true

            let selectedFilterMode: '' | 'bucketName' | 'presetFilter' | 'csaJobProfileExchange' = ''

            if (defaultSearchApiResponse.workSupplier) {
              this.workSupplier = defaultSearchApiResponse.workSupplier
            }

            const csa = defaultSearchApiResponse.scheduleParameters?.csa
            const jobProfile = defaultSearchApiResponse.scheduleParameters?.jobProfile
            const exchange = defaultSearchApiResponse.scheduleParameters?.exchange

            const workTypes = defaultSearchApiResponse.workTypes

            this.viewCapacity.pagination.itemsPerPage = defaultSearchApiResponse.count
            this.viewCapacity.sorting.sortingOrder = defaultSearchApiResponse.sortAscending ? 'sortAscending' : 'sortDescending'
            this.viewCapacity.sorting.sortingField = defaultSearchApiResponse.sortAscending ? defaultSearchApiResponse.sortAscending : defaultSearchApiResponse.sortDescending

            if (defaultSearchApiResponse.capacityBuckets) {
              selectedFilterMode = 'bucketName'
            } else if (defaultSearchApiResponse.scheduleParameters) {
              selectedFilterMode = 'csaJobProfileExchange'
            } else if (defaultSearchApiResponse.filters) {
              selectedFilterMode = 'presetFilter'
            }

            this.viewCapacity.filters.selectedFilterMode = selectedFilterMode

            this.viewCapacity.filters.selectedBuckets = defaultSearchApiResponse.capacityBuckets?.map((bucket: SearchCapacityBucketType) => bucket.capacityBucketId)

            this.viewCapacity.filters.selectedPresetFilter = defaultSearchApiResponse.filters?.map((filter: SearchFilterType) => filter.filterId) || []
            this.viewCapacity.filters.selectedCSA = csa ? csa.split(',') : []
            this.viewCapacity.filters.selectedJobProfiles = jobProfile ? jobProfile.split(',') : []
            this.viewCapacity.filters.selectedExchanges = exchange ? exchange.split(',') : []
            this.viewCapacity.filters.selectedWorkTypes = workTypes || []

            if (this.viewCapacity.filters.selectedBuckets && this.viewCapacity.filters.selectedBuckets.length > 0) {
              this.getBucketCapacityUtilisation()
            }
          }

          this.viewCapacity.showSpinner = false
        }
      }).catch((error: Error) => {
        console.error('error', error)
        this.viewCapacity.showSpinner = false
      })
    },

    clearCutOffTimesFilters () {
      this.cutOffTimes.filters.selectedFilterMode = ''
      this.cutOffTimes.filters.selectedCSA = []
      this.cutOffTimes.filters.selectedJobProfiles = []
      this.cutOffTimes.filters.selectedExchanges = []
      this.cutOffTimes.filters.selectedBuckets = []
      this.cutOffTimes.filters.selectedPresetFilter = []
    },

    clearViewBucketsResults () {
      this.viewCapacity.bucketsUtilisationRecords = []
    },

    clearViewBucketsFilters () {
      this.viewCapacity.filters.selectedFilterMode = ''
      this.viewCapacity.filters.selectedCSA = []
      this.viewCapacity.filters.selectedJobProfiles = []
      this.viewCapacity.filters.selectedExchanges = []
      this.viewCapacity.filters.selectedBuckets = []
      this.viewCapacity.filters.selectedPresetFilter = []
    },

    clearUpdateRAPFilters () {
      this.updateRAP.filters.selectedCSA = []
      this.updateRAP.filters.selectedJobProfiles = []
      this.updateRAP.filters.selectedExchanges = []
      this.updateRAP.filters.selectedBuckets = []
      this.updateRAP.filters.selectedPresetFilter = []
    },

    getMonthlyCapacityForBucket () {
      if (this.viewMonthlyCapacity.filters.selectedBuckets.length === 1) {
        this.viewMonthlyCapacity.showTableSpinner = true

        const bucketId = this.viewMonthlyCapacity.filters.selectedBuckets[0]
        const currentDate = DateTime.fromJSDate(this.viewMonthlyCapacity.filters.commonStartDateForCalendars)
        const datePlusOneMonth = currentDate.plus({ months: 1 })
        const datePlusTwoMonth = currentDate.plus({ months: 2 })

        const dates = [
          currentDate.toFormat('yyyy-MM'),
          datePlusOneMonth.toFormat('yyyy-MM'),
          datePlusTwoMonth.toFormat('yyyy-MM')
        ]

        // Make three API calls concurrently using Promise.all
        Promise.all(
          dates.map((date) => {
            return axios.get(
              `/cams-capacity-manager/v1/capacity-buckets/${bucketId}/capacity`,
              {
                params: {
                  month: `${date}`
                }
              }
            )
          })
        ).then((responses) => {
          // Combine results from all responses
          const combinedApiResponseData = responses.flatMap((response) => response.data) as ViewMonthlyCapacityAPIResponseType

          this.viewMonthlyCapacity.monthlyCapacityApiResponse = combinedApiResponseData

          const mappedData = mapViewMonthlyCapacityResponseToCalendarData(combinedApiResponseData)

          this.viewMonthlyCapacity.monthlyCapacityCalendarData = mappedData
          this.viewMonthlyCapacity.showTableSpinner = false
        }).catch((error: Error) => {
          console.log(error)
          this.viewMonthlyCapacity.showTableSpinner = false
        })
      }
    },
    getPresetFilters () {
      let params = {
        offset: 0,
        count: 100
      }
      if (userCompanyIsChorus()) {
        params = {
          ...params,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          workSupplier: this.workSupplier
        }
      }
      return axios.get('/cams-capacity-manager/v1/filters', {
        params
      })
    },
    getViewCapacityPresetFilters () {
      return this.getPresetFilters().then((response: AxiosResponse) => {
        this.viewCapacity.referenceOptions.filters = response.data
      })
    },
    getUpdateLeadTimePresetFilters () {
      return this.getPresetFilters().then((response: AxiosResponse) => {
        this.updateLeadTime.referenceOptions.filters = response.data
      })
    },
    getViewNextAvailableTimeSlotPresetFilters () {
      return this.getPresetFilters().then((response: AxiosResponse) => {
        this.viewNextAvailableTimeSlot.referenceOptions.filters = response.data
      })
    },
    getUpdateTimeslotAvailabilityPresetFilters () {
      return this.getPresetFilters().then((response: AxiosResponse) => {
        this.updateTimeslotAvailability.referenceOptions.filters = response.data
      })
    },
    getPresetFiltersListForViewPresetFiltersPage () {
      return this.getPresetFilters().then((response: AxiosResponse) => {
        this.presetFiltersPage.referenceOptions.filters = response.data
      })
    },
    getPresetFiltersListForCutOffTimesPage () {
      return this.getPresetFilters().then((response: AxiosResponse) => {
        this.cutOffTimes.referenceOptions.filters = response.data
      })
    },
    getUpdateBaselineCapacityPresetFilters () {
      return this.getPresetFilters().then((response: AxiosResponse) => {
        this.updateBaselineCapacity.referenceOptions.filters = response.data
      })
    },
    getUpdateCapacityPresetFilters () {
      return this.getPresetFilters().then((response: AxiosResponse) => {
        this.updateCapacity.referenceOptions.filters = response.data
      })
    },
    getUpdateRAPPresetFilters () {
      return this.getPresetFilters().then((response: AxiosResponse) => {
        this.updateRAP.referenceOptions.filters = response.data
      })
    },
    getExchangesListForSelectedCSA (CSAOptions: string[]) {
      let exchangesList: string[] = []

      if (CSAOptions.length > 0) {
        const csaExchangeItem = this.referenceData?.find((item) => {
          return item.name === 'CSA_EXCHANGE'
        })

        if (csaExchangeItem) {
          const csaExchange = csaExchangeItem.specification as CSAExchangeType

          CSAOptions.forEach((csa) => {
            const exchanges = csaExchange[csa]

            if (exchanges) {
              exchangesList = exchangesList.concat(exchanges)
            }
          })
        }
      }

      if (CSAOptions.length === 0) {
        exchangesList = this.exchangesList()
      }

      return exchangesList.sort()
    },
    getExchangesListOptionsForSelectedCSA (CSAOptions: string[]): OptionType[] {
      return this.getExchangesListForSelectedCSA(CSAOptions).map((exchange) => ({ label: exchange, value: exchange }))
    },
    serviceCompaniesList (): string[] {
      return this.getOptions('WORK_SUPPLIER')
    },
    csaList (): string[] {
      return this.getOptions('CSA')
    },
    csaListOptions (): OptionType[] {
      return this.csaList().map((csa) => ({ label: csa, value: csa }))
    },
    jobProfilesList (): string[] {
      return this.getOptions('JOB_PROFILE')
    },
    jobProfilesListOptions (): OptionType[] {
      return this.jobProfilesList().map((jobProfile) => ({ label: jobProfile, value: jobProfile }))
    },
    exchangesList (): string[] {
      return this.getOptions('EXCHANGE')
    },
    workTypesList (): string[] {
      return this.getOptions('WORK_TYPE')
    },
    workTypeListOptions (): OptionType[] {
      return this.workTypesList().map((workType) => ({ label: workType, value: workType }))
    },
    getOptions (optionName: string): string[] {
      /**
       * Generic function to extract options from reference data by option name
       */
      if (this.referenceData) {
        const list = this.referenceData.find((item: ReferenceDataRecordType) => item.name === optionName)
        if (list) {
          const specification = list.specification

          if (Array.isArray(specification)) {
            return specification.sort((a: string, b: string) => {
              if (a < b) {
                return -1
              }
              if (a > b) {
                return 1
              }
              return 0
            })
          }
        }
      }
      return []
    },
    getReferenceData () {
      return axios.get('/cams-capacity-manager/v1/reference-data', {
        params: {
          offset: 0,
          count: 50
        }
      }).then((response: AxiosResponse) => {
        this.referenceData = response.data
      })
    },

    getBucketsOptionsForPresetFiltersPage () {
      this.presetFiltersPage.addRemoveBucketsView.showSpinner = true
      this.presetFiltersPage.addRemoveBucketsView.referenceOptions.bucketOptions = []

      return this.getSortedBucketsOptions().then((bucketOptions: OptionType[]) => {
        this.presetFiltersPage.addRemoveBucketsView.referenceOptions.bucketOptions = bucketOptions
        this.presetFiltersPage.addRemoveBucketsView.showSpinner = false
      })
    },

    getBucketsOptionsForActivityHistoryPage () {
      this.activityHistory.showBucketsSpinner = true

      return this.getSortedBucketsOptions().then((bucketOptions: OptionType[]) => {
        this.activityHistory.referenceOptions.bucketOptions = bucketOptions
        this.activityHistory.showBucketsSpinner = false
      })
    },

    getBucketsOptionsForUpdateCapacityPage () {
      this.updateCapacity.showBucketsSpinner = true
      return this.getSortedBucketsOptions().then((bucketOptions: OptionType[]) => {
        this.updateCapacity.referenceOptions.bucketOptions = bucketOptions
        this.updateCapacity.showBucketsSpinner = false
      })
    },

    getBucketsOptionsForUpdateBaselineCapacityPage () {
      this.updateBaselineCapacity.showBucketsSpinner = true

      return this.getSortedBucketsOptions().then((bucketOptions: OptionType[]) => {
        this.updateBaselineCapacity.referenceOptions.bucketOptions = bucketOptions
        this.updateBaselineCapacity.showBucketsSpinner = false
      })
    },

    getBucketsOptionsForViewBucketsPage () {
      this.viewCapacity.showBucketsSpinner = true

      return this.getSortedBucketsOptions().then((bucketOptions: OptionType[]) => {
        this.viewCapacity.referenceOptions.bucketOptions = bucketOptions
        this.viewCapacity.showBucketsSpinner = false
      })
    },

    getBucketsOptionsForCutOffTimesPage () {
      this.cutOffTimes.showBucketsSpinner = true

      return this.getSortedBucketsOptions().then((bucketOptions: OptionType[]) => {
        this.cutOffTimes.referenceOptions.bucketOptions = bucketOptions
        this.cutOffTimes.showBucketsSpinner = false
      })
    },

    getBucketsOptionsForLeadTimesPage () {
      this.updateLeadTime.showSpinner = true

      return this.getBucketsOptions().then((response: AxiosResponse) => {
        // Work out all the options for the buckets name/short-name
        const records = response.data as CapacityBucketsSummaryResponseType
        if (records) {
          const recordsRemoOnly = records.filter(record => {
            return record.workType === 'REMO'
          })
          this.updateLeadTime.referenceOptions.bucketOptions = recordsRemoOnly.map(record => ({
            label: `${record.name} (${record.shortName})`,
            value: record.id
          })).sort((a, b) => a.label.localeCompare(b.label))
        }
        this.updateLeadTime.showSpinner = false
      })
    },

    getBucketsOptionsForViewMonthlyCapacityPage () {
      this.viewMonthlyCapacity.showBucketsSpinner = true

      return this.getSortedBucketsOptions().then((bucketOptions: OptionType[]) => {
        this.viewMonthlyCapacity.referenceOptions.bucketOptions = bucketOptions
        this.viewMonthlyCapacity.showBucketsSpinner = false
      })
    },

    getBucketsOptionsForViewNextAvailableTimeSlotPage () {
      this.viewNextAvailableTimeSlot.showBucketsSpinner = true
      return this.getSortedBucketsOptions().then((bucketOptions: OptionType[]) => {
        this.viewNextAvailableTimeSlot.referenceOptions.bucketOptions = bucketOptions
        this.viewNextAvailableTimeSlot.showBucketsSpinner = false
      })
    },

    getBucketsOptionsForUpdateTimeslotAvailabilityPage () {
      this.updateTimeslotAvailability.showBucketsSpinner = true

      return this.getSortedBucketsOptions().then((bucketOptions: OptionType[]) => {
        this.updateTimeslotAvailability.referenceOptions.bucketOptions = bucketOptions
        this.updateTimeslotAvailability.showBucketsSpinner = false
      })
    },

    getBucketsOptionsForUpdateRAPPage () {
      this.updateRAP.showBucketsSpinner = true

      return this.getBucketsOptions().then((response: AxiosResponse) => {
        // Work out all the options for the buckets name/short-name
        const records = response.data as CapacityBucketsSummaryResponseType
        if (records) {
          this.updateRAP.referenceOptions.bucketOptions = records.map(record => ({
            label: `${record.name} (${record.shortName})`,
            value: record.id
          })).sort((a, b) => a.label.localeCompare(b.label))
        }
        this.updateRAP.showBucketsSpinner = false
      })
    },

    getBucketsOptions () {
      let params = {
        offset: 0,
        count: 1000,
        view: 'summary'
      }
      if (userCompanyIsChorus()) {
        params = {
          offset: 0,
          count: 1000,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          workSupplier: this.workSupplier,
          view: 'summary'
        }
      }
      return axios.get('/cams-capacity-manager/v1/capacity-buckets', {
        params
      })
    },

    getSortedBucketsOptions (): Promise<OptionType[]> {
      return this.getBucketsOptions().then((response: AxiosResponse) => {
        const records = response.data as CapacityBucketsSummaryResponseType
        if (records) {
          return records.map(record => ({
            label: `${record.name} (${record.shortName})`,
            value: record.id
          })).sort((a, b) => a.label.localeCompare(b.label))
        }
        return []
      })
    },

    getBucketsWithCutOffTimes () {
      if (this.cutOffTimes.filters.selectedFilterMode === 'bucketName') {
        this.cutOffTimes.showBucketsTableSpinner = true

        return axios.post('/cams-capacity-manager/v1/capacity-buckets/query',
          { capacityBucketIds: this.cutOffTimes.filters.selectedBuckets },
          { params: this.allParametersViewBucketsWithCutOff }
        ).then((response: AxiosResponse) => {
          this.cutOffTimes.bucketsLevel2 = response.data
          this.cutOffTimes.showBucketsTableSpinner = false
          this.cutOffTimes.pagination.totalRecords = parseInt(response.headers['x-total-count'])
          this.cutOffTimes.pagination.totalPages = Math.ceil(this.cutOffTimes.pagination.totalRecords / this.cutOffTimes.pagination.itemsPerPage)
        })
      }

      // All other filter modes
      if (this.cutOffTimes.filters.selectedFilterMode === 'presetFilter' ||
          this.cutOffTimes.filters.selectedFilterMode === 'csaJobProfileExchange') {
        this.cutOffTimes.showBucketsTableSpinner = true

        return axios.get('/cams-capacity-manager/v1/capacity-buckets', {
          params: this.allParametersViewBucketsWithCutOff
        }).then((response: AxiosResponse) => {
          this.cutOffTimes.bucketsLevel2 = response.data
          this.cutOffTimes.showBucketsTableSpinner = false
          this.cutOffTimes.pagination.totalRecords = parseInt(response.headers['x-total-count'])
          this.cutOffTimes.pagination.totalPages = Math.ceil(this.cutOffTimes.pagination.totalRecords / this.cutOffTimes.pagination.itemsPerPage)
        })
      }
    },

    getBucketCapacityUtilisation () {
      if (this.viewCapacity.filters.selectedFilterMode === 'bucketName') {
        this.viewCapacity.showSpinner = true
        this.viewCapacity.disableSecondaryFilters = true

        return axios.post('/cams-capacity-manager/v1/capacity-buckets/query',
          { capacityBucketIds: this.viewCapacity.filters.selectedBuckets },
          { params: this.allParametersViewBuckets }
        ).then((response: AxiosResponse) => {
          this.viewCapacity.bucketsUtilisationRecords = response.data
          this.viewCapacity.showSpinner = false
          this.viewCapacity.pagination.totalRecords = parseInt(response.headers['x-total-count'])
          this.viewCapacity.pagination.totalPages = Math.ceil(this.viewCapacity.pagination.totalRecords / this.viewCapacity.pagination.itemsPerPage)
          this.viewCapacity.disableSecondaryFilters = false
        })
      }

      // All other filter modes
      if (this.viewCapacity.filters.selectedFilterMode === 'presetFilter' ||
          this.viewCapacity.filters.selectedFilterMode === 'csaJobProfileExchange') {
        this.viewCapacity.showSpinner = true
        this.viewCapacity.disableSecondaryFilters = true

        return axios.get('/cams-capacity-manager/v1/capacity-buckets', {
          params: this.allParametersViewBuckets
        }).then((response: AxiosResponse) => {
          this.viewCapacity.bucketsUtilisationRecords = response.data
          this.viewCapacity.showSpinner = false
          this.viewCapacity.pagination.totalRecords = parseInt(response.headers['x-total-count'])
          this.viewCapacity.pagination.totalPages = Math.ceil(this.viewCapacity.pagination.totalRecords / this.viewCapacity.pagination.itemsPerPage)
          this.viewCapacity.disableSecondaryFilters = false
        })
      }
    },

    getBucketsForPresetFiltersAddRemoveView () {
      this.presetFiltersPage.addRemoveBucketsView.showSpinner = true
      this.presetFiltersPage.addRemoveBucketsView.disableSecondaryFilters = true
      if (this.presetFiltersPage.addRemoveBucketsView.filters.selectedFilterMode === 'bucketName') {
        return axios.post('/cams-capacity-manager/v1/capacity-buckets/query',
          { capacityBucketIds: this.presetFiltersPage.addRemoveBucketsView.filters.selectedBuckets },
          { params: this.allParametersGetBucketsForEditPresetFiltersPage }
        ).then((response: AxiosResponse) => {
          const apiResponseData = response.data

          const mappedBucketOptions = apiResponseData.map((bucket: BucketUtilisationRecordType) => {
            return {
              label: `${bucket.name} (${bucket.shortName})`,
              value: bucket.id
            }
          })

          // Filter out the buckets that are already in the preset filter
          const availableBuckets = mappedBucketOptions.filter((bucket: OptionType) => {
            return !this.presetFiltersPage.addRemoveBucketsView.presetFilterBuckets.some((presetFilterBucket) => {
              return presetFilterBucket.value === bucket.value
            })
          })

          this.presetFiltersPage.addRemoveBucketsView.availableBuckets = availableBuckets

          this.presetFiltersPage.addRemoveBucketsView.showSpinner = false
          this.presetFiltersPage.addRemoveBucketsView.disableSecondaryFilters = false
        })
      }

      if (this.presetFiltersPage.addRemoveBucketsView.filters.selectedFilterMode === 'presetFilter' ||
        this.presetFiltersPage.addRemoveBucketsView.filters.selectedFilterMode === 'csaJobProfileExchange') {
        return axios.get('/cams-capacity-manager/v1/capacity-buckets', {
          params: this.allParametersGetBucketsForEditPresetFiltersPage
        }).then((response: AxiosResponse) => {
          const apiResponseData = response.data

          const mappedBucketOptions = apiResponseData.map((bucket: BucketUtilisationRecordType) => {
            return {
              label: `${bucket.name} (${bucket.shortName})`,
              value: bucket.id
            }
          })

          // Filter out the buckets that are already in the preset filter
          const availableBuckets = mappedBucketOptions.filter((bucket: OptionType) => {
            return !this.presetFiltersPage.addRemoveBucketsView.presetFilterBuckets.some((presetFilterBucket) => {
              return presetFilterBucket.value === bucket.value
            })
          })

          this.presetFiltersPage.addRemoveBucketsView.availableBuckets = availableBuckets

          this.presetFiltersPage.addRemoveBucketsView.showSpinner = false
          this.presetFiltersPage.addRemoveBucketsView.disableSecondaryFilters = false
        })
      }
    },

    getBucketsForPresetFilter () {
      let params = {
        offset: this.presetFiltersPage.previewTable.filters.offset.toString(),
        count: this.presetFiltersPage.previewTable.filters.count.toString(),
        filterIds: this.presetFiltersPage.previewTable.currentActiveFilter?.value,
        view: 'summary'
      }
      if (userCompanyIsChorus()) {
        params = {
          ...params,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          workSupplier: this.workSupplier
        }
      }

      const url = '/cams-capacity-manager/v1/capacity-buckets'

      this.presetFiltersPage.previewTable.showSpinner = true
      return axios.get(url, { params }).then((response: AxiosResponse) => {
        this.presetFiltersPage.buckets = response.data as CapacityBucketsSummaryResponseType
        this.presetFiltersPage.previewTable.pagination.totalRecords = parseInt(response.headers['x-total-count'])
        this.presetFiltersPage.previewTable.pagination.totalPages = Math.ceil(this.presetFiltersPage.previewTable.pagination.totalRecords / this.presetFiltersPage.previewTable.pagination.itemsPerPage)
        this.presetFiltersPage.previewTable.showSpinner = false
      }).catch((error: AxiosError) => {
        console.error(error)
        this.presetFiltersPage.previewTable.showSpinner = false
      })
    },

    getAllBucketsForPresetFilters () {
      this.presetFiltersPage.addRemoveBucketsView.showSpinner = true

      const targetFilterId = this.presetFiltersPage.previewTable.currentActiveFilter?.value
      const url = `cams-capacity-manager/v1/filters/${targetFilterId}`
      return axios.get(url).then((response: AxiosResponse) => {
        const capacityBuckets = response.data.capacityBuckets as CapacityBucketsSummaryResponseType

        this.presetFiltersPage.addRemoveBucketsView.presetFilterBuckets = capacityBuckets.map(item => {
          return {
            label: `${item.name} (${item.shortName})`,
            value: item.id
          }
        })

        this.presetFiltersPage.addRemoveBucketsView.showSpinner = false
      }).catch((error: AxiosError) => {
        console.error(error)
        this.presetFiltersPage.addRemoveBucketsView.showSpinner = false
      })
    },

    getBucketCapacityUtilisationForUpdateTimeslotAvailability () {
      if (this.updateTimeslotAvailability.filters.selectedFilterMode === 'bucketName') {
        this.updateTimeslotAvailability.showSpinner = true
        this.updateTimeslotAvailability.disableSecondaryFilters = true

        return axios.post('/cams-capacity-manager/v1/capacity-buckets/query',
          { capacityBucketIds: this.updateTimeslotAvailability.filters.selectedBuckets },
          { params: this.allParametersUpdateTimeslotAvailability }
        ).then((response: AxiosResponse) => {
          const apiResponseData = response.data as CapacityBucketsSummaryResponseType
          this.updateTimeslotAvailability.bucketsUtilisationRecords = mapUpdateTimeslotAvailabilityResponseToRecordType(apiResponseData)

          this.updateTimeslotAvailability.showSpinner = false
          this.updateTimeslotAvailability.pagination.totalRecords = parseInt(response.headers['x-total-count'])
          this.updateTimeslotAvailability.pagination.totalPages = Math.ceil(this.updateTimeslotAvailability.pagination.totalRecords / this.updateTimeslotAvailability.pagination.itemsPerPage)
          this.updateTimeslotAvailability.disableSecondaryFilters = false
        })
      }

      if (this.updateTimeslotAvailability.filters.selectedFilterMode === 'presetFilter' ||
          this.updateTimeslotAvailability.filters.selectedFilterMode === 'csaJobProfileExchange') {
        this.updateTimeslotAvailability.showSpinner = true
        this.updateTimeslotAvailability.disableSecondaryFilters = true

        return axios.get('/cams-capacity-manager/v1/capacity-buckets', {
          params: this.allParametersUpdateTimeslotAvailability
        }).then((response: AxiosResponse) => {
          const apiResponseData = response.data as CapacityBucketsSummaryResponseType
          this.updateTimeslotAvailability.bucketsUtilisationRecords = mapUpdateTimeslotAvailabilityResponseToRecordType(apiResponseData)

          this.updateTimeslotAvailability.showSpinner = false
          this.updateTimeslotAvailability.pagination.totalRecords = parseInt(response.headers['x-total-count'])
          this.updateTimeslotAvailability.pagination.totalPages = Math.ceil(this.updateTimeslotAvailability.pagination.totalRecords / this.updateTimeslotAvailability.pagination.itemsPerPage)
          this.updateTimeslotAvailability.disableSecondaryFilters = false
        })
      }
    },

    getSelectedBucketsForUpdateCapacity () {
      if (this.updateCapacity.filters.selectedFilterMode === 'bucketName') {
        this.updateCapacity.showBucketsTableSpinner = true
        this.updateCapacity.disableSecondaryFilters = true

        return axios.post('/cams-capacity-manager/v1/capacity-buckets/query',
          { capacityBucketIds: this.updateCapacity.filters.selectedBuckets },
          { params: this.allParametersUpdateCapacity }
        ).then((response: AxiosResponse) => {
          this.updateCapacity.bucketsUtilisationRecords = response.data
          this.updateCapacity.showBucketsTableSpinner = false
          this.updateCapacity.pagination.totalRecords = parseInt(response.headers['x-total-count'])
          this.updateCapacity.pagination.totalPages = Math.ceil(this.updateCapacity.pagination.totalRecords / this.updateCapacity.pagination.itemsPerPage)
          this.updateCapacity.disableSecondaryFilters = false
        })
      }

      if (this.updateCapacity.filters.selectedFilterMode === 'presetFilter' ||
          this.updateCapacity.filters.selectedFilterMode === 'csaJobProfileExchange') {
        this.updateCapacity.showBucketsTableSpinner = true
        this.updateCapacity.disableSecondaryFilters = true

        return axios.get('/cams-capacity-manager/v1/capacity-buckets', {
          params: this.allParametersUpdateCapacity
        }).then((response: AxiosResponse) => {
          this.updateCapacity.bucketsUtilisationRecords = response.data
          this.updateCapacity.showBucketsTableSpinner = false
          this.updateCapacity.pagination.totalRecords = parseInt(response.headers['x-total-count'])
          this.updateCapacity.pagination.totalPages = Math.ceil(this.updateCapacity.pagination.totalRecords / this.updateCapacity.pagination.itemsPerPage)
          this.updateCapacity.disableSecondaryFilters = false
        })
      }
    },

    getBucketsForUpdateRAP () {
      if (this.updateRAP.filters.selectedFilterMode === 'bucketName') {
        this.updateRAP.showSpinner = true
        this.updateRAP.disableSecondaryFilters = true

        return axios.post('/cams-capacity-manager/v1/capacity-buckets/query',
          { capacityBucketIds: this.updateRAP.filters.selectedBuckets },
          { params: this.allParametersUpdateRAP }
        ).then((response: AxiosResponse) => {
          const responseData = response.data as CapacityBucketsSummaryResponseType
          const mappedTableData = mapRAPResponseToTableRow(responseData)

          this.updateRAP.currentBuckets = mappedTableData
          this.updateRAP.originalBuckets = structuredClone(mappedTableData)

          this.updateRAP.showSpinner = false
          this.updateRAP.pagination.totalRecords = parseInt(response.headers['x-total-count'])
          this.updateRAP.pagination.totalPages = Math.ceil(this.updateRAP.pagination.totalRecords / this.updateRAP.pagination.itemsPerPage)
          this.updateRAP.disableSecondaryFilters = false
        })
      }

      // All other filter modes
      if (this.updateRAP.filters.selectedFilterMode === 'presetFilter' ||
          this.updateRAP.filters.selectedFilterMode === 'csaJobProfileExchange') {
        this.updateRAP.showSpinner = true
        this.updateRAP.disableSecondaryFilters = true

        return axios.get('/cams-capacity-manager/v1/capacity-buckets', {
          params: this.allParametersUpdateRAP
        }).then((response: AxiosResponse) => {
          const responseData = response.data as CapacityBucketsSummaryResponseType
          const mappedTableData = mapRAPResponseToTableRow(responseData)

          this.updateRAP.currentBuckets = mappedTableData
          this.updateRAP.originalBuckets = structuredClone(mappedTableData)

          this.updateRAP.showSpinner = false
          this.updateRAP.pagination.totalRecords = parseInt(response.headers['x-total-count'])
          this.updateRAP.pagination.totalPages = Math.ceil(this.updateRAP.pagination.totalRecords / this.updateRAP.pagination.itemsPerPage)
          this.updateRAP.disableSecondaryFilters = false
        })
      }
    },

    setFiltersForBucketOnUpdateCapacityPage (
      filters: FiltersType,
      selectedBucketId: string,
      bucketsOptions: OptionType[],
      startDate: Date = new Date(),
      endDate: Date = new Date()
    ) {
      // Helper action to be used in meatballs menus to jump into the update capacity page for one bucket
      const selectedBucketOption = bucketsOptions.filter((bucket) => bucket.value === selectedBucketId)
      const selectedBuckets = [selectedBucketId]

      this.updateCapacity.referenceOptions.bucketOptions = bucketsOptions
      this.updateCapacity.referenceOptions.bucketOptionsForEditing = selectedBucketOption
      this.updateCapacity.filters = {
        ...this.updateCapacity.filters,
        selectedPresetFilter: filters.selectedPresetFilter,
        selectedCSA: filters.selectedCSA,
        selectedJobProfiles: filters.selectedJobProfiles,
        selectedExchanges: filters.selectedExchanges,
        selectedFilterMode: 'bucketName',
        selectedBucketIdForEditingUpdateCapacity: selectedBucketId,
        selectedBucketIdsForEditTableDropdown: [selectedBucketId],
        selectedOptionsForBucketsList: [selectedBucketId],
        selectedStartDate: startDate,
        selectedEndDate: endDate,
        selectedBuckets
      }
    },

    getBucketCapacityUtilisationForUpdateBaselineCapacity () {
      if (this.updateBaselineCapacity.filters.selectedFilterMode === 'bucketName') {
        this.updateBaselineCapacity.showSpinner = true
        this.updateBaselineCapacity.disableSecondaryFilters = true

        return axios.post('/cams-capacity-manager/v1/capacity-buckets/query',
          { capacityBucketIds: this.updateBaselineCapacity.filters.selectedBuckets },
          { params: this.allParametersUpdateBaselineCapacity }
        ).then((response: AxiosResponse) => {
          this.updateBaselineCapacity.bucketsUtilisationRecords = response.data
          this.updateBaselineCapacity.showSpinner = false
          this.updateBaselineCapacity.pagination.totalRecords = parseInt(response.headers['x-total-count'])
          this.updateBaselineCapacity.pagination.totalPages = Math.ceil(this.updateBaselineCapacity.pagination.totalRecords / this.updateBaselineCapacity.pagination.itemsPerPage)
          this.updateBaselineCapacity.disableSecondaryFilters = false

          this.updateBaselineCapacity.referenceOptions.bucketOptionsForEditing = []
          this.updateBaselineCapacity.filters.selectedBucketIdForEditing = ''
          this.updateBaselineCapacity.baselineCapacityTimeslotsApiResponse = null
          this.updateBaselineCapacity.originalBaselineCapacityTimeslotsTableData = null
          this.updateBaselineCapacity.currentBaselineCapacityTimeslotsTableData = null
        })
      }

      if (this.updateBaselineCapacity.filters.selectedFilterMode === 'presetFilter' ||
          this.updateBaselineCapacity.filters.selectedFilterMode === 'csaJobProfileExchange') {
        this.updateBaselineCapacity.showSpinner = true
        this.updateBaselineCapacity.disableSecondaryFilters = true

        return axios.get('/cams-capacity-manager/v1/capacity-buckets', {
          params: this.allParametersUpdateBaselineCapacity
        }).then((response: AxiosResponse) => {
          this.updateBaselineCapacity.bucketsUtilisationRecords = response.data
          this.updateBaselineCapacity.showSpinner = false
          this.updateBaselineCapacity.pagination.totalRecords = parseInt(response.headers['x-total-count'])
          this.updateBaselineCapacity.pagination.totalPages = Math.ceil(this.updateBaselineCapacity.pagination.totalRecords / this.updateBaselineCapacity.pagination.itemsPerPage)
          this.updateBaselineCapacity.disableSecondaryFilters = false

          this.updateBaselineCapacity.referenceOptions.bucketOptionsForEditing = []
          this.updateBaselineCapacity.filters.selectedBucketIdForEditing = ''
          this.updateBaselineCapacity.baselineCapacityTimeslotsApiResponse = null
          this.updateBaselineCapacity.originalBaselineCapacityTimeslotsTableData = null
          this.updateBaselineCapacity.currentBaselineCapacityTimeslotsTableData = null
        })
      }
    },

    getBucketsWithNextAvailable () {
      if (this.viewNextAvailableTimeSlot.filters.selectedFilterMode === 'bucketName') {
        this.viewNextAvailableTimeSlot.showTableSpinner = true
        this.viewNextAvailableTimeSlot.disableSecondaryFilters = true

        return axios.post('/cams-capacity-manager/v1/capacity-buckets/query',
          { capacityBucketIds: this.viewNextAvailableTimeSlot.filters.selectedBuckets },
          { params: this.allParametersNextTimeslotAvailability }
        ).then((response: AxiosResponse) => {
          const timeslotsApiResponse = response.data as NextAvailableTimeslotApiResponseType

          const nextAvailableTimeslot = this.viewNextAvailableTimeSlot
          const pagination = this.viewNextAvailableTimeSlot.pagination

          const mappedData = mapNextAvailableTimeslotsResponseToTableData(timeslotsApiResponse)

          nextAvailableTimeslot.timeslotsApiResponse = timeslotsApiResponse
          nextAvailableTimeslot.timeslotsTableData = mappedData

          pagination.totalRecords = parseInt(response.headers['x-total-count'])
          pagination.totalPages = Math.ceil(pagination.totalRecords / pagination.itemsPerPage)

          nextAvailableTimeslot.showTableSpinner = false
          this.viewNextAvailableTimeSlot.disableSecondaryFilters = false
        })
      }

      if (this.viewNextAvailableTimeSlot.filters.selectedFilterMode === 'presetFilter' ||
          this.viewNextAvailableTimeSlot.filters.selectedFilterMode === 'csaJobProfileExchange') {
        this.viewNextAvailableTimeSlot.showTableSpinner = true
        this.viewNextAvailableTimeSlot.disableSecondaryFilters = true

        return axios.get('/cams-capacity-manager/v1/capacity-buckets', {
          params: this.allParametersNextTimeslotAvailability
        }).then((response: AxiosResponse) => {
          const timeslotsApiResponse = response.data as NextAvailableTimeslotApiResponseType

          const nextAvailableTimeslot = this.viewNextAvailableTimeSlot
          const pagination = this.viewNextAvailableTimeSlot.pagination

          const mappedData = mapNextAvailableTimeslotsResponseToTableData(timeslotsApiResponse)

          nextAvailableTimeslot.timeslotsApiResponse = timeslotsApiResponse
          nextAvailableTimeslot.timeslotsTableData = mappedData

          pagination.totalRecords = parseInt(response.headers['x-total-count'])
          pagination.totalPages = Math.ceil(pagination.totalRecords / pagination.itemsPerPage)

          nextAvailableTimeslot.showTableSpinner = false
          this.viewNextAvailableTimeSlot.disableSecondaryFilters = false
        })
      }
    },

    getUpdateBaselineCapacityTimeslotsForSelectedBucket () {
      this.updateBaselineCapacity.showCurrentBaselineTableSpinner = true
      const selectedBucketId = this.updateBaselineCapacity.filters.selectedBucketIdForEditing

      const url = `/cams-capacity-manager/v1/capacity-buckets/${selectedBucketId}`

      return axios.get(url).then((response: AxiosResponse) => {
        const timeslotsApiResponse = response.data.timeslotStructure as BaselineCapacityTimeslotApiResponseType
        const sortedTimeslotsApiResponse = sortBaselineCapacityTimeslotsByWeekday(timeslotsApiResponse)

        // const timeslotsApiResponse = UpdateCapacityTimeSlotsApiResponseMocked as BaselineCapacityTimeslotApiResponseType

        this.updateBaselineCapacity.baselineCapacityTimeslotsApiResponse = sortedTimeslotsApiResponse

        const tableData = mapBaselineCapacityResponseToTableData(sortedTimeslotsApiResponse)

        this.updateBaselineCapacity.currentBaselineCapacityTimeslotsTableData = tableData

        // User editable table data
        this.updateBaselineCapacity.originalBaselineCapacityTimeslotsTableData = structuredClone(tableData)
        this.updateBaselineCapacity.showCurrentBaselineTableSpinner = false
      }).catch((error: Error) => {
        this.updateBaselineCapacity.showCurrentBaselineTableSpinner = false
        console.error('error', error)
      })
    },

    saveChangesToBaselineCapacity () {
      this.updateBaselineCapacity.showCurrentBaselineTableSpinner = true
      const selectedBucketId = this.updateBaselineCapacity.filters.selectedBucketIdForEditing
      const payload = this.uploadBaselineCapacityTimeslotsPayload

      const url = `/cams-capacity-manager/v1/capacity-buckets/${selectedBucketId}`

      return axios.patch(url, payload).then((response: AxiosResponse) => {
        if (response.status === 200) {
          const updatedCapacityBucketId = response.data.id
          this.updateBaselineCapacity.updatedCapacityBucketIds.push(updatedCapacityBucketId)

          this.getUpdateBaselineCapacityTimeslotsForSelectedBucket()
          this.updateBaselineCapacity.showSuccessPopup = true
          this.updateBaselineCapacity.showCurrentBaselineTableSpinner = false
          this.updateBaselineCapacity.tableIsInEditMode = false
        }
      }).catch((error: Error) => {
        console.error('error', error)
        this.updateBaselineCapacity.showCurrentBaselineTableSpinner = false
      })
    },

    getSlotsForSelectedBucket () {
      this.updateCapacity.tableIsInEditMode = false
      this.updateCapacity.showTableSpinner = true
      const selectedBucketId = this.updateCapacity.filters.selectedBucketIdForEditingUpdateCapacity

      let checkSelectedEndDate = this.updateCapacity.filters.selectedEndDate
      const activationPeriod = this.updateCapacity.filters.selectedBucketActivationPeriod

      if (checkSelectedEndDate === null) {
        const defaultSelectedEndDate = DateTime.now().plus({ days: activationPeriod })
        checkSelectedEndDate = defaultSelectedEndDate.toJSDate()
      }

      if (this.updateCapacity.filters.selectedStartDate) {
        const selectedStartDate = DateTime.fromJSDate(this.updateCapacity.filters.selectedStartDate).toFormat('yyyy-MM-dd')
        const selectedEndDate = DateTime.fromJSDate(checkSelectedEndDate).toFormat('yyyy-MM-dd')

        const url = `/cams-capacity-manager/v1/capacity-buckets/${selectedBucketId}/slots`

        return axios.get(url, {
          params: {
            startDate: selectedStartDate,
            endDate: selectedEndDate
          }
        }).then((response: AxiosResponse) => {
          const selectedWeekdayFilters = this.updateCapacity.filters.selectedDays.map(day => {
            return parseInt(day)
          })

          const data = response.data as TimeSlotsAPIResponseType
          const filteredData = data
            .filter(data => {
              if (selectedWeekdayFilters.length === 0) return true

              const weekday = formatDayOfWeek(data.startDateTime)
              const weekdayNumber = weekdayNumberFromName(weekday)
              return selectedWeekdayFilters.includes(weekdayNumber)
            })
          // const data = TimeSlotsApiResponseMocked as TimeSlotsAPIResponseType
          this.updateCapacity.apiResponse = filteredData

          const mappedData = mapUpdateCapacityResponseToTableData(filteredData)

          // Update both on when we fetching new data (We will use this to compare changes)
          this.updateCapacity.originalMappedTableData = mappedData
          // User editable data
          this.updateCapacity.currentMappedTableData = structuredClone(mappedData)

          this.updateCapacity.showTableSpinner = false
        })
      }
    },

    getActivationPeriodForUpdateCapacitySelectedBucket () {
      if (!this.updateCapacity.filters.selectedBucketIdForEditingUpdateCapacity) return

      const selectedBucketId = this.updateCapacity.filters.selectedBucketIdForEditingUpdateCapacity
      const url = `/cams-capacity-manager/v1/capacity-buckets/${selectedBucketId}`

      return axios.get(url)
        .then((response: AxiosResponse) => {
          const data = response.data as ScheduleBucketAPIResponseType

          if (data.rollingConfiguration !== undefined) {
            const rollingTimeslotActivationPeriod = data.rollingConfiguration.furthestRollingTimeslotActivationPeriod
            const activationPeriod = getActivationPeriodAsNumber(rollingTimeslotActivationPeriod)

            // Current activation period should include today's date, so we subtract 1
            this.updateCapacity.filters.selectedBucketActivationPeriod = activationPeriod - 1
          }
        })
        .catch((error: Error) => {
          console.error('error', error)
        })
    },

    getActivationPeriodForMonthlyCapacitySelectedBucket () {
      if (this.viewMonthlyCapacity.filters.selectedBuckets.length === 0) return

      const selectedBucketId = this.viewMonthlyCapacity.filters.selectedBuckets[0]
      const url = `/cams-capacity-manager/v1/capacity-buckets/${selectedBucketId}`

      return axios.get(url)
        .then((response: AxiosResponse) => {
          const data = response.data as ScheduleBucketAPIResponseType

          if (data.rollingConfiguration !== undefined) {
            const rollingTimeslotActivationPeriod = data.rollingConfiguration.furthestRollingTimeslotActivationPeriod
            const activationPeriod = getActivationPeriodAsNumber(rollingTimeslotActivationPeriod)

            // Current activation period should include today's date, so we subtract 1
            this.viewMonthlyCapacity.filters.selectedBucketActivationPeriod = activationPeriod - 1
          }
        })
        .catch((error: Error) => {
          console.error('error', error)
        })
    },

    getUpdateBaselineCapacityActivationPeriodForSelectedBucket () {
      if (this.updateBaselineCapacity.filters.selectedBucketIdForEditing === '') return

      const selectedBucketId = this.updateBaselineCapacity.filters.selectedBucketIdForEditing
      const url = `/cams-capacity-manager/v1/capacity-buckets/${selectedBucketId}`

      return axios.get(url)
        .then((response: AxiosResponse) => {
          const data = response.data as ScheduleBucketAPIResponseType

          if (data.rollingConfiguration !== undefined) {
            const rollingTimeslotActivationPeriod = data.rollingConfiguration.furthestRollingTimeslotActivationPeriod
            const activationPeriod = getActivationPeriodAsNumber(rollingTimeslotActivationPeriod)

            // Current activation period should include today's date, so we subtract 1
            this.updateBaselineCapacity.filters.selectedBucketActivationPeriod = activationPeriod - 1
          }
        })
        .catch((error: Error) => {
          console.error('error', error)
        })
    },

    saveCapacityChanges () {
      this.updateCapacity.showUpdateSpinner = true
      const payload = this.updateTimeSlotCapacityPayload
      const selectedBucketId = this.updateCapacity.filters.selectedBucketIdForEditingUpdateCapacity

      const url = `/cams-capacity-manager/v1/capacity-buckets/${selectedBucketId}/slots`

      return axios.patch(url, payload)
        .then((response: AxiosResponse) => {
          if (response.status === 204) {
            this.updateCapacity.updatedCapacityBucketIds.push(selectedBucketId)

            this.updateCapacity.updateTimeSlotCapacityPayload = {} as TimeslotUpdatePayloadType
            this.updateCapacity.originalMappedTableData = JSON.parse(JSON.stringify(this.updateCapacity.currentMappedTableData))

            this.updateCapacity.showSuccessPopup = true
            this.updateCapacity.showUpdateSpinner = false
            this.updateCapacity.tableIsInEditMode = false
          }
        })
        .catch((error) => {
          // Need to do something with this Error
          console.log('Error: ', error)
          this.updateCapacity.showUpdateSpinner = false
          this.updateCapacity.tableIsInEditMode = false
        })
    },

    previewTimeslotAvailabilityChanges () {
      this.updateTimeslotAvailability.showUpdateSpinner = true
      const payload = this.updateTimeSlotAvailabilityPayload

      const url = '/cams-capacity-manager/v1/capacity-buckets/slots/preview'

      return axios.post(url, payload)
        .then((response: AxiosResponse) => {
          if (response.status === 200) {
            const data = response.data as TimeSlotsAPIResponseType
            this.updateTimeslotAvailability.timeslotPreviewApiResponse = data

            const selectedBucketIds = this.updateTimeslotAvailability.filters.selectedBucketsToChange
            const previewTableData = mapUpdateTimeslotAvailabilityResponseToTableData(data, selectedBucketIds)

            this.updateTimeslotAvailability.timeslotAvailabilityPreviewData = previewTableData

            this.updateTimeslotAvailability.showUpdateSpinner = false
            this.updateTimeslotAvailability.showUpdatePopup = false
            this.updateTimeslotAvailability.showPreviewUpdatePopup = true
          }
        })
        .catch((error) => {
          console.log('Error: ', error)
          this.updateTimeslotAvailability.showUpdateSpinner = false
          this.updateTimeslotAvailability.showUpdatePopup = false
        })
    },

    previewBulkEditTimeslotCapacityChanges (): void {
      this.updateCapacity.showUpdateSpinner = true
      this.updateCapacity.showBulkEditPreviewWarningMessage = false

      const originalTableData = this.updateCapacity.originalMappedTableData
      // Reset to original table when user re-selects new timerange options
      this.updateCapacity.bulkEdit.currentMappedTableData = JSON.parse(JSON.stringify(originalTableData))

      const currentTableData = this.updateCapacity.bulkEdit.currentMappedTableData
      const bulkEditMaxUnits = this.updateCapacity.bulkEdit.bulkEditMaxTotalUnits
      const selectedBulkEditOption = this.updateCapacity.bulkEdit.selectedBulkEditOption
      const selectedTimeRanges = this.updateCapacity.bulkEdit.selectedBulkEditTimeRanges

      for (const timeRangeKey in currentTableData) {
        const timeRangedIsSelected = selectedTimeRanges.includes(timeRangeKey)

        if (timeRangedIsSelected || selectedTimeRanges.length === 0) {
          const timeRangeData = currentTableData[timeRangeKey]
          for (const dateKey in timeRangeData) {
            const timeslotData = timeRangeData[dateKey]

            const targetDate = DateTime.fromISO(dateKey)
            const now = DateTime.local()

            const endOfCurrentTimeRange = targetDate.set({
              hour: Number(timeRangeKey.split('-')[1].split(':')[0]),
              minute: Number(timeRangeKey.split('-')[1].split(':')[1])
            })

            if (timeslotData.status === 'INACTIVE') {
              // Skip inactive timeslots
              this.updateCapacity.showBulkEditPreviewWarningMessage = true
              continue
            }

            if (endOfCurrentTimeRange < now) {
              // Skip past timeslots and timeslots that are within time range
              continue
            }

            // Apply bulk edit logic based on selected option
            if (selectedBulkEditOption === 'Exact number') {
              timeslotData.maxTotal = bulkEditMaxUnits
              timeslotData.available = (bulkEditMaxUnits - timeslotData.bookedCapacity)
            } else if (selectedBulkEditOption === 'Add units') {
              const newMaxTotal = Math.max(0, timeslotData.maxTotal + bulkEditMaxUnits)
              timeslotData.maxTotal = newMaxTotal
              timeslotData.available = (newMaxTotal - timeslotData.bookedCapacity)
            } else if (selectedBulkEditOption === 'Remove units') {
              const newMaxTotal = Math.max(0, timeslotData.maxTotal - bulkEditMaxUnits)
              timeslotData.maxTotal = newMaxTotal
              timeslotData.available = (newMaxTotal - timeslotData.bookedCapacity)
            }
          }
        }
      }

      this.updateCapacity.showBulkEditPopup = false
      this.updateCapacity.showBulkEditTimeslotPreviewPopup = true
      this.updateCapacity.showUpdateSpinner = false
    },

    previewForPushToActiveData () {
      this.updateBaselineCapacity.showPushToActiveSpinner = true

      const selectedBucketId = this.updateBaselineCapacity.filters.selectedBucketIdForEditing
      const selectedStartDate = this.updateBaselineCapacity.filters.selectedPopupStartDate
      const selectedWeekDays = this.updateBaselineCapacity.filters.selectedWeekDays

      const defaultWeekDays = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']
      const selectedWeekDaysParam = selectedWeekDays.length === 0 ? defaultWeekDays : selectedWeekDays

      const payload = {
        updateFromBaseline: {
          startDate: DateTime.fromJSDate(selectedStartDate).toFormat('yyyy-MM-dd'),
          daysOfWeek: selectedWeekDaysParam
        }
      }

      const url = `/cams-capacity-manager/v1/capacity-buckets/${selectedBucketId}/slots/preview`

      return axios.post(url, payload)
        .then((response: AxiosResponse) => {
          if (response.status === 200) {
            const timeslotsApiResponse = response.data as TimeSlotsAPIResponseType

            const activeTimeslots = timeslotsApiResponse.filter((timeslot) => timeslot.status !== 'PROPOSED')
            const sortedTimeslots = activeTimeslots.sort((a, b) => {
              return a.startDateTime.localeCompare(b.startDateTime)
            })

            const mappedData = mapUpdateCapacityResponseToTableData(sortedTimeslots)

            this.updateBaselineCapacity.previewApiResponse = sortedTimeslots
            this.updateBaselineCapacity.previewTableData = mappedData

            this.updateBaselineCapacity.showPushToActivePopup = false
            this.updateBaselineCapacity.showPreviewTablePopup = true
            this.updateBaselineCapacity.showPushToActiveSpinner = false
          }
        })
        .catch((error) => {
          console.log('Error: ', error)
          this.updateBaselineCapacity.showPushToActiveSpinner = false
        })
    },

    saveChangedForPushToActiveData () {
      this.updateBaselineCapacity.showPushToActiveSpinner = true

      const selectedBucketId = this.updateBaselineCapacity.filters.selectedBucketIdForEditing
      const selectedStartDate = this.updateBaselineCapacity.filters.selectedPopupStartDate
      const selectedWeekDays = this.updateBaselineCapacity.filters.selectedWeekDays

      const defaultWeekDays = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']
      const selectedWeekDaysParam = selectedWeekDays.length === 0 ? defaultWeekDays : selectedWeekDays

      const payload = {
        updateFromBaseline: {
          startDate: DateTime.fromJSDate(selectedStartDate).toFormat('yyyy-MM-dd'),
          daysOfWeek: selectedWeekDaysParam
        }
      }
      const url = `/cams-capacity-manager/v1/capacity-buckets/${selectedBucketId}/slots`

      return axios.patch(url, payload)
        .then((response: AxiosResponse) => {
          if (response.status === 204) {
            this.updateBaselineCapacity.pushToActiveDataResultMsg = 'success'
            this.updateBaselineCapacity.showPushToActiveSpinner = false
          }
        })
        .catch((error) => {
          console.log('Error: ', error)
          this.updateBaselineCapacity.pushToActiveDataResultMsg = 'error'
          this.updateBaselineCapacity.showPushToActiveSpinner = false
        })
    },

    saveBulkEditTimeslotCapacity () {
      this.updateCapacity.showUpdateSpinner = true
      const payload = this.updateTimeSlotCapacityEditBulkCapacityData
      const selectedBucketId = this.updateCapacity.filters.selectedBucketIdForEditingUpdateCapacity

      const url = `/cams-capacity-manager/v1/capacity-buckets/${selectedBucketId}/slots`

      return axios.patch(url, payload)
        .then((response: AxiosResponse) => {
          if (response.status === 204) {
            this.getSlotsForSelectedBucket()

            this.updateCapacity.updatedCapacityBucketIds.push(selectedBucketId)

            this.updateCapacity.showUpdateSpinner = false
            this.updateCapacity.showUpdateMessage = 'success'
            this.updateCapacity.showBulkEditPreviewWarningMessage = false
          }
        })
        .catch((error) => {
          console.log('Error: ', error)
          this.updateCapacity.showUpdateMessage = 'error'
          this.updateCapacity.showUpdateSpinner = false
          this.updateCapacity.showBulkEditPreviewWarningMessage = false
        })
    },

    saveTimeslotAvailabilityChanges () {
      this.updateTimeslotAvailability.showUpdateSpinner = true
      const payload = this.updateTimeSlotAvailabilityPayload

      const url = '/cams-capacity-manager/v1/capacity-buckets/slots'

      return axios.patch(url, payload)
        .then((response: AxiosResponse) => {
          if (response.status === 200) {
            this.updateTimeslotAvailability.timeslotPreviewApiResponse = []
            this.updateTimeslotAvailability.timeslotAvailabilityPreviewData = {}

            const updatedCapacityBucketId = response.data
            updatedCapacityBucketId.forEach((updatedCapacityBucketId: string) => {
              this.updateTimeslotAvailability.updatedCapacityBucketIds.push(updatedCapacityBucketId)
            })

            this.updateTimeslotAvailability.showUpdateMessage = 'success'
            this.updateTimeslotAvailability.showUpdateSpinner = false
          }
        })
        .catch((error) => {
          console.log('Error: ', error)
          this.updateTimeslotAvailability.showUpdateMessage = 'error'
          this.updateTimeslotAvailability.showUpdateSpinner = false
        })
    },

    saveDefaultViewForUserProfile () {
      this.viewCapacity.showUpdateSpinner = false

      const payload = this.saveDefaultViewForUserProfilePayload

      const url = '/cams-capacity-manager/v1/profiles/me'
      return axios.patch(url, payload).then((response: AxiosResponse) => {
        if (response.status === 200) {
          this.viewCapacity.defaultFiltersSet = true
          this.viewCapacity.showDefaultViewUpdateStatus = 'success'
          this.viewCapacity.showUpdateSpinner = false
        }
      }).catch((error: Error) => {
        console.error('error', error)
        this.viewCapacity.showDefaultViewUpdateStatus = 'error'
        this.viewCapacity.showUpdateSpinner = false
      })
    },

    resetDefaultViewFilters () {
      this.viewCapacity.showUpdateSpinner = false

      const url = '/cams-capacity-manager/v1/profiles/me'
      return axios.patch(url, { defaultSearch: {} }).then((response: AxiosResponse) => {
        if (response.status === 200) {
          this.viewCapacity.defaultFiltersSet = false
          this.viewCapacity.showDefaultViewUpdateStatus = 'success'
          this.viewCapacity.showUpdateSpinner = false
        }
      }).catch((error: Error) => {
        console.error('error', error)
        this.viewCapacity.showDefaultViewUpdateStatus = 'error'
        this.viewCapacity.showUpdateSpinner = false
      })
    },

    createPresetFilter () {
      const url = '/cams-capacity-manager/v1/filters'
      let payload = {
        name: this.presetFiltersPage.createPopup.name,
        capacityBucketIds: []
      }

      if (userCompanyIsChorus()) {
        payload = {
          ...payload,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          workSupplier: this.workSupplier.toUpperCase()
        }
      }
      axios.post(url, payload).then((response: AxiosResponse) => {
        const newFilterId = response.data.id as string
        const newSelectedFilterLabel = response.data.name as string
        // refresh list of filters and select the new one
        this.getPresetFiltersListForViewPresetFiltersPage().then(() => {
          this.presetFiltersPage.previewTable.filters.filterId = newFilterId
          this.presetFiltersPage.selectedFilterLabel = newSelectedFilterLabel
          this.presetFiltersPage.buckets = []
          this.presetFiltersPage.createPopup.show = false
          this.presetFiltersPage.createPopup.showSpinner = false
          this.presetFiltersPage.viewMode = 'edit'

          this.presetFiltersPage.previewTable.pagination.totalRecords = 0
          this.presetFiltersPage.previewTable.pagination.totalPages = 0
          this.presetFiltersPage.previewTable.showSpinner = false

          this.presetFiltersPage.previewTable.currentActiveFilter = {
            value: newFilterId,
            label: newSelectedFilterLabel
          }

          // Reset edit view
          this.presetFiltersPage.addRemoveBucketsView.presetFilterBuckets = []
          this.presetFiltersPage.addRemoveBucketsView.availableBuckets = []
          this.presetFiltersPage.addRemoveBucketsView.filters.selectedFilterMode = 'bucketName'
          this.presetFiltersPage.addRemoveBucketsView.filters.selectedBuckets = []
          this.presetFiltersPage.addRemoveBucketsView.filters.selectedPresetFilter = []
          this.presetFiltersPage.addRemoveBucketsView.filters.selectedCSA = []
          this.presetFiltersPage.addRemoveBucketsView.filters.selectedJobProfiles = []
          this.presetFiltersPage.addRemoveBucketsView.filters.selectedExchanges = []
        })
      }).catch((error: AxiosError) => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const errorDetails = error.response.data as APIErrorResponseType
        switch (errorDetails.errorCode) {
          case 'CCMA-400-302':
            // Duplicate filter name
            this.presetFiltersPage.createPopup.apiErrors = [
              'This filter name already exists. Change the name to continue'
            ]
            this.presetFiltersPage.createPopup.showSpinner = false
            break

          default:
            this.globalErrorPopup.errorDetails = errorDetails
            this.globalErrorPopup.showPopup = true
            this.globalErrorPopup.errorMessage = 'Error creating preset filter'
            this.presetFiltersPage.createPopup.showSpinner = false
            this.presetFiltersPage.createPopup.show = false
            this.presetFiltersPage.createPopup.name = ''
        }
      })
    },

    renamePresetFilter (filterId: string) {
      const url = `/cams-capacity-manager/v1/filters/${filterId}`
      const payload = {
        name: this.presetFiltersPage.renamePopup.name
      }
      axios.patch(url, payload).then((response: AxiosResponse) => {
        const newSelectedFilterLabel = response.data.name as string
        // refresh list of filters and select the new one
        this.getPresetFiltersListForViewPresetFiltersPage().then(() => {
          this.presetFiltersPage.renamePopup.show = false
          this.presetFiltersPage.renamePopup.showSpinner = false
          this.presetFiltersPage.selectedFilterLabel = newSelectedFilterLabel
          this.presetFiltersPage.previewTable.filters.filterId = filterId
        })
      }).catch((error: AxiosError) => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const errorDetails = error.response.data as APIErrorResponseType
        switch (errorDetails.errorCode) {
          case 'CCMA-400-302':
            // Duplicate filter name
            this.presetFiltersPage.renamePopup.apiErrors = [
              'This filter name already exists. Change the name to continue'
            ]
            this.presetFiltersPage.renamePopup.showSpinner = false
            break

          default:
            this.globalErrorPopup.errorDetails = errorDetails
            this.globalErrorPopup.showPopup = true
            this.globalErrorPopup.errorMessage = 'Error renaming preset filter'
            this.presetFiltersPage.renamePopup.showSpinner = false
            this.presetFiltersPage.renamePopup.show = false
            this.presetFiltersPage.renamePopup.name = ''
        }
      })
    },
    deletePresetFilter (filterId: string) {
      const url = `/cams-capacity-manager/v1/filters/${filterId}`
      axios.delete(url).then(() => {
        // refresh list of filters and select the first one
        this.getPresetFiltersListForViewPresetFiltersPage().then(() => {
          this.presetFiltersPage.buckets = null
          this.presetFiltersPage.deletePopup.show = false
          this.presetFiltersPage.deletePopup.showSpinner = false
        })
      }).catch((error: AxiosError) => {
        this.presetFiltersPage.deletePopup.show = false
        this.presetFiltersPage.deletePopup.showSpinner = false
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        this.globalErrorPopup.errorDetails = error.response.data
        this.globalErrorPopup.showPopup = true
        this.globalErrorPopup.errorMessage = 'Error deleting preset filter'
      })
    },
    updatePresetFilter (filterId: string, capacityBucketIds: string[]) {
      this.presetFiltersPage.addRemoveBucketsView.showSpinner = true
      const url = `/cams-capacity-manager/v1/filters/${filterId}`
      const payload = {
        capacityBucketIds
      }
      axios.patch(url, payload).then(() => {
        this.presetFiltersPage.addRemoveBucketsView.showSaveFilterSuccessPopup = true
        this.presetFiltersPage.addRemoveBucketsView.showSpinner = false
      }).catch((error: AxiosError) => {
        this.presetFiltersPage.addRemoveBucketsView.showSpinner = false
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        this.globalErrorPopup.errorDetails = error.response.data
        this.globalErrorPopup.showPopup = true
        this.globalErrorPopup.errorMessage = 'Error updating preset filter'
      })
    },
    saveBulkEditRAP () {
      if (!this.updateRAP.previewBuckets) {
        return
      }
      this.updateRAP.bulkEdit.showUpdateSpinner = true
      const payload = this.updateRAP.previewBuckets.map(bucket => {
        return {
          capacityBucketId: bucket.id,
          rollingConfiguration: {
            rollingTimeslotActivationPeriod: bucket.rollingTimeslotActivationPeriod
          }
        }
      })

      const url = '/cams-capacity-manager/v1/capacity-buckets/rolling-activation'

      return axios.patch(url, payload)
        .then((response: AxiosResponse) => {
          if (response.status === 204) {
            this.getBucketsForUpdateRAP()

            this.updateRAP.bulkEdit.showUpdateSpinner = false
            this.updateRAP.showBulkEditPreviewPopup = false
            this.updateRAP.bulkEdit.showSuccessPopup = true
          }
        })
        .catch((error: AxiosError) => {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          const errorDetails = error.response.data as APIErrorResponseType
          this.globalErrorPopup.errorDetails = errorDetails
          this.globalErrorPopup.showPopup = true
          this.globalErrorPopup.errorMessage = 'Can\'t save RAP changes'
          this.updateRAP.showBulkEditPreviewPopup = false
          this.updateRAP.bulkEdit.showUpdateSpinner = false
        })
    },

    saveIndividualEditRAP (changedBuckets: RAPBucketRow[]) {
      this.updateRAP.showSpinner = true

      // filter out the buckets that have been not changed
      const payload = changedBuckets.map(bucket => {
        return {
          capacityBucketId: bucket.id,
          rollingConfiguration: {
            rollingTimeslotActivationPeriod: bucket.rollingTimeslotActivationPeriod
          }
        }
      })

      const url = '/cams-capacity-manager/v1/capacity-buckets/rolling-activation'

      return axios.patch(url, payload)
        .then((response: AxiosResponse) => {
          if (response.status === 204) {
            this.getBucketsForUpdateRAP()

            this.updateRAP.showSpinner = false
            this.updateRAP.bulkEdit.showSuccessPopup = true
          }
        }).catch((error: AxiosError) => {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          const errorDetails = error.response.data as APIErrorResponseType
          this.globalErrorPopup.errorDetails = errorDetails
          this.globalErrorPopup.showPopup = true
          this.globalErrorPopup.errorMessage = 'Can\'t save RAP changes'
          this.updateRAP.showSpinner = false
        })
    },
    getBucketsOptionsForPresetFilter (filterIds: string[]) {
      const url = '/cams-capacity-manager/v1/capacity-buckets'
      let params = {
        offset: 0,
        count: 1000,
        filterIds: filterIds.join(','),
        view: 'summary'

      }
      if (userCompanyIsChorus()) {
        params = {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          workSupplier: this.workSupplier,
          ...params
        }
      }

      return axios.get(url, { params })
    },
    getLeadTimesForBucket (capacityBucketId: string) {
      this.updateLeadTime.showSpinner = true
      const url = '/cams-capacity-manager/v1/capacity-buckets/lead-times'
      const params = {
        capacityBucketId
      }
      axios.get(url, { params }).then((response: AxiosResponse) => {
        const leadTimes = response.data as LeadTimeRecordType[]
        this.updateLeadTime.leadTimesRecords = leadTimes
        this.updateLeadTime.leadTimesCellsOriginal = getLeadTimesTableData(leadTimes)
        this.updateLeadTime.tableData = prepareLeadTimeTableData(this.updateLeadTime.leadTimesCellsOriginal)

        this.updateLeadTime.exchanges = getExchangesFromLeadTimes(leadTimes)
        this.updateLeadTime.jobProfiles = getJobProfilesFromLeadTimes(leadTimes)
        this.updateLeadTime.showSpinner = false
      }).catch((error: AxiosError) => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const errorDetails = error.response.data as APIErrorResponseType
        this.globalErrorPopup.errorDetails = errorDetails
        this.globalErrorPopup.showPopup = true
        this.globalErrorPopup.errorMessage = 'Can\'t fetch lead time for bucket'
        this.updateLeadTime.showSpinner = false
      })
    },
    getLeadTimesForFilters () {
      this.updateLeadTime.showSpinner = true
      const url = '/cams-capacity-manager/v1/capacity-buckets/lead-times'
      let params = {
        jobProfiles: this.updateLeadTime.filters.selectedJobProfiles.join(','),
        exchanges: this.updateLeadTime.filters.selectedExchanges.join(','),
        csa: this.updateLeadTime.filters.selectedCSA[0] || ''
      }
      if (userCompanyIsChorus()) {
        params = {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          workSupplier: this.workSupplier,
          ...params
        }
      }
      axios.get(url, { params }).then((response: AxiosResponse) => {
        const leadTimes = response.data as LeadTimeRecordType[]
        this.updateLeadTime.leadTimesRecords = leadTimes
        this.updateLeadTime.leadTimesCellsOriginal = getLeadTimesTableData(leadTimes)
        this.updateLeadTime.tableData = prepareLeadTimeTableData(this.updateLeadTime.leadTimesCellsOriginal)

        this.updateLeadTime.exchanges = getExchangesFromLeadTimes(leadTimes)
        this.updateLeadTime.jobProfiles = getJobProfilesFromLeadTimes(leadTimes)
        this.updateLeadTime.showSpinner = false
      }).catch((error: AxiosError) => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const errorDetails = error.response.data as APIErrorResponseType
        this.globalErrorPopup.errorDetails = errorDetails
        this.globalErrorPopup.showPopup = true
        this.globalErrorPopup.errorMessage = 'Can\'t fetch lead times'
        this.updateLeadTime.showSpinner = false
      })
    },
    saveLeadTimesUpdates (payload: UpdateLeadTimesPayload) {
      const url = '/cams-capacity-manager/v1/capacity-buckets/lead-times'
      this.updateLeadTime.showSpinner = true
      axios.patch(url, payload).then((response: AxiosResponse) => {
        this.updateLeadTime.showSpinner = false
        const responsePayload = response.data
        if (responsePayload.warnings && responsePayload.warnings.length > 0) {
          const warnings = responsePayload.warnings.map((warning: { warningType: string, description: string }) => {
            return warning.description
          })
          this.globalErrorPopup.errorMessage = warnings.join('<br>')
          this.globalErrorPopup.title = 'Warning'
          this.globalErrorPopup.errorDetails = null
          this.globalErrorPopup.showPopup = true
        } else {
          this.updateLeadTime.showSuccessPopup = true
        }
      }).catch((error: AxiosError) => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const errorDetails = error.response.data as APIErrorResponseType
        this.globalErrorPopup.errorDetails = errorDetails
        this.globalErrorPopup.showPopup = true
        this.globalErrorPopup.errorMessage = 'System error - can\'t update lead times'
        this.updateLeadTime.showSpinner = false
      })
    },
    saveBulkLeadTimesUpdates (payload: UpdateLeadTimesPayload) {
      const url = '/cams-capacity-manager/v1/capacity-buckets/lead-times'
      this.updateLeadTime.bulkEditPreviewPopup.showSpinner = true
      axios.patch(url, payload).then(() => {
        this.updateLeadTime.showSuccessPopup = true
        this.updateLeadTime.bulkEditPreviewPopup.showPopup = false
        this.updateLeadTime.bulkEditPreviewPopup.showSpinner = false
      }).catch((error: AxiosError) => {
        this.updateLeadTime.bulkEditPreviewPopup.showPopup = false
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const errorDetails = error.response.data as APIErrorResponseType
        this.globalErrorPopup.errorDetails = errorDetails
        this.globalErrorPopup.showPopup = true
        this.globalErrorPopup.errorMessage = 'System error - can\'t update lead times'
        this.updateLeadTime.bulkEditPreviewPopup.showSpinner = false
      })
    },
    saveCutOffTimesForSingleBucket (payload: UpdateCutOffTimesForBucketPayload) {
      const url = '/cams-capacity-manager/v1/capacity-buckets/cut-off-times'
      this.cutOffTimes.showSaveCutOffTimeForBucketSpinner = true
      return axios.patch(url, payload).then((response: AxiosResponse) => {
        this.cutOffTimes.showSaveCutOffTimeForBucketSpinner = false
        this.cutOffTimes.bucketIsInEditMode = false
        const responsePayload = response.data
        if (responsePayload.warnings && responsePayload.warnings.length > 0) {
          const warnings = responsePayload.warnings.map((warning: { warningType: string, description: string }) => {
            return warning.description
          })
          this.globalErrorPopup.errorMessage = warnings.join('<br>')
          this.globalErrorPopup.title = 'Warning'
          this.globalErrorPopup.errorDetails = null
          this.globalErrorPopup.showPopup = true
        } else {
          this.cutOffTimes.showSaveSuccessPopup = true
        }
      }).catch((error: AxiosError) => {
        this.cutOffTimes.showSaveCutOffTimeForBucketSpinner = false
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const errorDetails = error.response.data as APIErrorResponseType
        switch (errorDetails.errorCode) {
          case 'CCMA-400-402':
            // invalid activation, start or end time
            this.globalErrorPopup.title = 'Invalid cut off time'
            this.globalErrorPopup.errorMessage = errorDetails.cause
            this.globalErrorPopup.errorDetails = null
            break

          case 'CCMA-400-401':
            // invalid activation, start or end time
            this.globalErrorPopup.title = 'No matching timeslots found'
            this.globalErrorPopup.errorMessage = errorDetails.cause
            this.globalErrorPopup.errorDetails = null
            break

          case 'CCMA-400-400':
            // Duplicates
            this.globalErrorPopup.title = 'Duplicate cut-off time'
            this.globalErrorPopup.errorMessage = errorDetails.cause
            this.globalErrorPopup.errorDetails = null
            break

          default:
            this.globalErrorPopup.errorMessage = 'Something went wrong - can\'t update cut off times. Try again later.'
            this.globalErrorPopup.title = 'System error'
            this.globalErrorPopup.errorDetails = errorDetails
        }
        this.globalErrorPopup.showPopup = true
      })
    },
    bulkSaveCutOffTimesForBuckets (payload: UpdateCutOffTimesForBucketPayload) {
      const url = '/cams-capacity-manager/v1/capacity-buckets/cut-off-times'
      this.cutOffTimes.bulkEditPopup.showSpinner = true
      return axios.patch(url, payload).then(() => {
        this.cutOffTimes.bulkEditPopup.showSuccessMessage = true
        this.cutOffTimes.bulkEditPopup.showSpinner = false
      }).catch((error: AxiosError) => {
        this.cutOffTimes.bulkEditPopup.showSpinner = false
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const errorDetails = error.response.data as APIErrorResponseType
        switch (errorDetails.errorCode) {
          case 'CCMA-400-402':
            // invalid activation, start or end time
            this.globalErrorPopup.errorMessage = errorDetails.cause
            this.globalErrorPopup.title = 'Invalid cut off time'
            this.globalErrorPopup.errorDetails = null
            break

          case 'CCMA-400-401':
            // invalid activation, start or end time
            this.globalErrorPopup.errorMessage = errorDetails.cause
            this.globalErrorPopup.title = 'No matching timeslots found'
            this.globalErrorPopup.errorDetails = null
            break

          case 'CCMA-400-400':
            // Duplicates
            this.globalErrorPopup.title = 'Duplicate cut-off time'
            this.globalErrorPopup.errorMessage = `${errorDetails.cause}`
            this.globalErrorPopup.errorDetails = null
            break

          default:
            this.globalErrorPopup.errorMessage = 'Something went wrong - can\'t update cut off times. Try again later.'
            this.globalErrorPopup.title = 'System error'
            this.globalErrorPopup.errorDetails = errorDetails
        }
        this.globalErrorPopup.showPopup = true
      })
    },

    getScheduleStructure () {
      this.viewSchedules.showDiagramSpinner = true
      this.viewSchedules.apiResponse = null
      // remove empty filters

      const cleanParams = Object.fromEntries(
        Object.entries(this.viewSchedules.filters).filter(([_key, value]) => {
          return value !== ''
        })
      )

      const url = '/cams-capacity-manager/v1/schedules'
      return axios.get(url, { params: cleanParams }).then((response: AxiosResponse) => {
        this.viewSchedules.apiResponse = response.data as ViewScheduleAPIResponseType
      }).catch((error: AxiosError) => {
        // show global error by default
        this.globalErrorPopup.errorDetails = error.response?.data as APIErrorResponseType
        this.globalErrorPopup.errorMessage = 'Error fetching schedule structure'
        this.globalErrorPopup.showPopup = true
      })
    },
    getDiagramBlockName (blockId: string, blockType: string) : string {
      const apiResponse = this.viewSchedules.apiResponse as ViewScheduleAPIResponseType
      // it could be changed, so we get it from store to make sure it's the latest one
      switch (blockType) {
        case 'capacity-group': {
          const group = apiResponse.capacityGroups.find((group) => {
            return group.id === blockId
          })
          if (group) {
            return group.name
          }
          break
        }

        case 'capacity-pool': {
          const pool = apiResponse.capacityPools.find((pool) => {
            return pool.id === blockId
          })
          if (pool) {
            return pool.name
          }
          break
        }

        case 'capacity-container': {
          const container = apiResponse.containers.find((container) => {
            return container.id === blockId
          })
          if (container) {
            return container.name
          }
          break
        }
      }
      return ''
    },
    updatePopupTitle (title: string) {
      this.viewSchedules.detailsPopup.title = `${this.viewSchedules.detailsPopup.titlePrefix} - ${title}`
    },
    addFieldToDetailsPopup (label: string, value: string, defaultValue = 'FAKE DATA HERE') {
      this.viewSchedules.detailsPopup.blockDetailsPopupFields.push(
        { label, value: value || defaultValue }
      )
    },
    getContainerDetails (containerId: string) {
      this.viewSchedules.detailsPopup.blockDetailsPopupFields = []
      if (this.viewSchedules.apiResponse) {
        const targetElement = this.viewSchedules.apiResponse.containers.find((container) => {
          return container.id === containerId
        })
        if (targetElement) {
          this.addFieldToDetailsPopup('Container name', targetElement.name)
          this.addFieldToDetailsPopup('Status', targetElement.status)
          this.addFieldToDetailsPopup('Start Date Time', dateTimeNzFormat(targetElement.startDateTime))
          this.addFieldToDetailsPopup('End Date Time', dateTimeNzFormat(targetElement.endDateTime))
          this.updatePopupTitle(targetElement.name)
        }
      }
      this.viewSchedules.detailsPopup.showSpinner = false
    },
    getPoolDetails (poolId: string) {
      this.viewSchedules.detailsPopup.blockDetailsPopupFields = []
      if (this.viewSchedules.apiResponse) {
        const targetElement = this.viewSchedules.apiResponse.capacityPools.find((pool) => {
          return pool.id === poolId
        })
        if (targetElement) {
          this.addFieldToDetailsPopup('Pool name', targetElement.name)
          this.addFieldToDetailsPopup('Status', targetElement.status)
          this.addFieldToDetailsPopup('Start Date Time', dateTimeNzFormat(targetElement.startDateTime))
          this.addFieldToDetailsPopup('End Date Time', dateTimeNzFormat(targetElement.endDateTime))
          this.updatePopupTitle(targetElement.name)
        }
      }

      this.viewSchedules.detailsPopup.showSpinner = false
    },
    getGroupDetails (groupId: string) {
      this.viewSchedules.detailsPopup.blockDetailsPopupFields = []
      if (this.viewSchedules.apiResponse) {
        const targetElement = this.viewSchedules.apiResponse.capacityGroups.find((group) => {
          return group.id === groupId
        })
        if (targetElement) {
          this.addFieldToDetailsPopup('Group name', targetElement.name)
          this.addFieldToDetailsPopup('Status', targetElement.status)
          this.addFieldToDetailsPopup('Start Date Time', dateTimeNzFormat(targetElement.startDateTime))
          this.addFieldToDetailsPopup('End Date Time', dateTimeNzFormat(targetElement.endDateTime))
          this.updatePopupTitle(targetElement.name)
        }
      }

      this.viewSchedules.detailsPopup.showSpinner = false
    },
    getBucketDetails (bucketId: string) {
      this.viewSchedules.detailsPopup.blockDetailsPopupFields = []

      if (this.viewSchedules.apiResponse) {
        const targetElement = this.viewSchedules.apiResponse.capacityBuckets.find((bucket) => {
          return bucket.id === bucketId
        })
        if (targetElement) {
          this.addFieldToDetailsPopup('Bucket short name', targetElement.shortName, '-')
          this.addFieldToDetailsPopup('Bucket long name', targetElement.name, '-')
          this.addFieldToDetailsPopup('Status', targetElement.status)
          let rollingTimeslotCreationPeriod = '-'
          if (targetElement.rollingConfiguration) {
            rollingTimeslotCreationPeriod = targetElement.rollingConfiguration.rollingTimeslotActivationPeriod.toString() + ' days'
          }

          this.addFieldToDetailsPopup('Rolling Activation Period', rollingTimeslotCreationPeriod)
          this.addFieldToDetailsPopup('Start Date Time', dateTimeNzFormat(targetElement.startDateTime))
          this.addFieldToDetailsPopup('End Date Time', dateTimeNzFormat(targetElement.endDateTime))
          this.updatePopupTitle(targetElement.name)
        }
      }

      this.viewSchedules.detailsPopup.showSpinner = false
    },
    getRuleDetails (ruleId: string) {
      this.viewSchedules.detailsPopup.blockDetailsPopupFields = []
      if (this.viewSchedules.apiResponse) {
        const allStructureElements = [
          ...this.viewSchedules.apiResponse.capacityBuckets,
          ...this.viewSchedules.apiResponse.capacityGroups,
          ...this.viewSchedules.apiResponse.capacityPools,
          ...this.viewSchedules.apiResponse.containers
        ]
        const allRulesMap = new Map<string, RuleType>()
        allStructureElements.forEach((element) => {
          element.rules.forEach((rule) => {
            allRulesMap.set(rule.id, rule)
          })
        })

        if (allRulesMap.has(ruleId)) {
          const targetRule = allRulesMap.get(ruleId) as RuleType
          this.addFieldToDetailsPopup('Rule name', targetRule.name)
          if (targetRule.match && targetRule.match.field) {
            const fieldLabel = targetRule.match.field.toLowerCase().replace('_', ' ')
            const fieldValue = targetRule.match.value
            this.addFieldToDetailsPopup(fieldLabel, fieldValue)
          }

          this.addFieldToDetailsPopup('Status', targetRule.state)
          this.addFieldToDetailsPopup('Start Date Time', dateTimeNzFormat(targetRule.startDateTime))
          this.addFieldToDetailsPopup('End Date Time', dateTimeNzFormat(targetRule.endDateTime))
          this.updatePopupTitle(targetRule.name)
        }
      }

      this.viewSchedules.detailsPopup.showSpinner = false
    },

    getErrorsHistoryRequestParamaters (): Map<string, string> {
      const filters = this.errorsHistory.filters
      const pagination = this.errorsHistory.pagination
      const paramsMap = new Map<string, string>()
      paramsMap.set('category', 'ERROR')

      const offset = (pagination.currentPage - 1) * pagination.itemsPerPage
      paramsMap.set('offset', offset.toString())
      paramsMap.set('count', pagination.itemsPerPage.toString())

      if (filters.startDate) {
        const startDateTime = getApiUTCDateTimeString(filters.startDate)
        paramsMap.set('startDateTime', startDateTime)
      }

      if (userCompanyIsChorus()) {
        paramsMap.set('workSupplier', this.workSupplier)
      }

      if (filters.endDate) {
        paramsMap.set('endDateTime', getApiUTCDateTimeString(filters.endDate))
      } else {
        if (filters.startDate) {
          const now = new Date()
          let endDate = new Date(filters.startDate)
          endDate.setMonth(endDate.getMonth() + 3)
          if (endDate > now) {
            endDate = now
          }
          paramsMap.set('endDateTime', getApiUTCDateTimeString(endDate))
        }
      }
      return paramsMap
    },

    getActivityHistoryRequestParameters (): Map<string, string> {
      const filters = this.activityHistory.filters
      const pagination = this.activityHistory.pagination
      const paramsMap = new Map<string, string>()
      paramsMap.set('category', 'ACTIVITY')

      const offset = (pagination.currentPage - 1) * pagination.itemsPerPage
      paramsMap.set('offset', offset.toString())
      paramsMap.set('count', pagination.itemsPerPage.toString())

      if (filters.startDate) {
        const startDateTime = getApiUTCDateTimeString(filters.startDate)
        paramsMap.set('startDateTime', startDateTime)
      }

      if (filters.capacityBucketId.length > 0) {
        paramsMap.set('capacityBucketId', filters.capacityBucketId[0].toString())
      }

      if (userCompanyIsChorus()) {
        paramsMap.set('workSupplier', this.workSupplier)
      }

      if (filters.userName) {
        paramsMap.set('username', filters.userName)
      }

      if (filters.activityType) {
        paramsMap.set('type', filters.activityType)
      }

      if (filters.endDate) {
        paramsMap.set('endDateTime', getApiUTCDateTimeString(filters.endDate))
      } else {
        if (filters.startDate) {
          const now = new Date()
          let endDate = new Date(filters.startDate)
          endDate.setMonth(endDate.getMonth() + 3)
          if (endDate > now) {
            endDate = now
          }
          paramsMap.set('endDateTime', getApiUTCDateTimeString(endDate))
        }
      }

      return paramsMap
    },

    getErrorsHistory () {
      this.errorsHistory.showTableSpinner = true
      const url = '/cams-capacity-manager/v1/events'

      const paramsMap = this.getErrorsHistoryRequestParamaters()
      const params = Object.fromEntries(paramsMap)

      return axios.get(url, { params }).then((response: AxiosResponse) => {
        this.errorsHistory.errorsList = response.data as ErrorRecordType[]
        this.errorsHistory.pagination.totalRecords = parseInt(response.headers['x-total-count'])
        this.errorsHistory.pagination.totalPages = Math.ceil(this.errorsHistory.pagination.totalRecords / this.errorsHistory.pagination.itemsPerPage)
        this.errorsHistory.showTableSpinner = false
      }).catch((error: AxiosError) => {
        this.errorsHistory.errorsList = null
        this.errorsHistory.showTableSpinner = false
        this.globalErrorPopup.errorDetails = error.response?.data as APIErrorResponseType
        this.globalErrorPopup.errorMessage = 'Error fetching errors history'
        this.globalErrorPopup.showPopup = true
      })
    },

    getErrorsForAlertBanner () {
      if (userHasCMAdminRole() || userHasCMUpdaterRole()) {
        const url = '/cams-capacity-manager/v1/events/recent-errors'

        // Get errors for last 72 hours
        return axios.get(url, {}).then((response: AxiosResponse) => {
          const errorsList = response.data as ErrorRecordType[]
          this.alertBanner.errors = errorsList.map((error) => {
            return {
              id: error.id,
              transactionId: error.transactionId,
              description: error.description
            }
          })
        }).catch((error: AxiosError) => {
          this.alertBanner.errors = []
          this.globalErrorPopup.errorDetails = error.response?.data as APIErrorResponseType
          this.globalErrorPopup.errorMessage = 'Error fetching errors for alert banner'
          this.globalErrorPopup.showPopup = true
        })
      }
    },

    getActivityHistory () {
      this.activityHistory.showTableSpinner = true
      const url = '/cams-capacity-manager/v1/events'
      const paramsMap = this.getActivityHistoryRequestParameters()
      const params = Object.fromEntries(paramsMap)

      return axios.get(url, { params }).then((response: AxiosResponse) => {
        this.activityHistory.activityList = response.data as ActivityRecordType[]
        this.activityHistory.pagination.totalRecords = parseInt(response.headers['x-total-count'])
        this.activityHistory.pagination.totalPages = Math.ceil(this.activityHistory.pagination.totalRecords / this.activityHistory.pagination.itemsPerPage)
        this.activityHistory.showTableSpinner = false
      }).catch((error: AxiosError) => {
        this.activityHistory.activityList = null
        this.activityHistory.showTableSpinner = false
        this.globalErrorPopup.errorDetails = error.response?.data as APIErrorResponseType
        this.globalErrorPopup.errorMessage = 'Error fetching activity history'
        this.globalErrorPopup.showPopup = true
      })
    },

    exportErrorsAsCSV () {
      const url = '/cams-capacity-manager/v1/events'
      const paramsMap = this.getErrorsHistoryRequestParamaters()
      paramsMap.set('csv', 'true')
      paramsMap.set('offset', '0')
      paramsMap.set('count', '10000')
      const params = Object.fromEntries(paramsMap)
      axios.get(url, { params, responseType: 'blob' }).then((response: AxiosResponse) => {
        const blob = new Blob([response.data], { type: 'text/csv' })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `errors_export__${getTimestampSuffixForFilename()}.csv`
        a.click()
      }).catch((error: AxiosError) => {
        this.globalErrorPopup.errorDetails = error.response?.data as APIErrorResponseType
        this.globalErrorPopup.errorMessage = 'Error exporting errors'
        this.globalErrorPopup.showPopup = true
      })
    },

    exportActivitiesAsCSV () {
      const url = '/cams-capacity-manager/v1/events'
      const paramsMap = this.getActivityHistoryRequestParameters()
      paramsMap.set('csv', 'true')
      paramsMap.set('offset', '0')
      paramsMap.set('count', '10000')
      const params = Object.fromEntries(paramsMap)
      axios.get(url, { params, responseType: 'blob' }).then((response: AxiosResponse) => {
        const blob = new Blob([response.data], { type: 'text/csv' })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `activities_export__${getTimestampSuffixForFilename()}.csv`
        a.click()
      }).catch((error: AxiosError) => {
        this.globalErrorPopup.errorDetails = error.response?.data as APIErrorResponseType
        this.globalErrorPopup.errorMessage = 'Error exporting activities'
        this.globalErrorPopup.showPopup = true
      })
    }

  }
})

export const store = useMainCMStore(activePinia)
